// TraitementThermique.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { Scanner } from "@yudiel/react-qr-scanner";

import { useI18n } from "../i18n/I18nContext";
import {
  calcHumiditeSechageStack,
  calcHumiditeCondStack,
  calcHumiditeCondPli,
  calcHumiditeImpPli,
  propagateHumiditeCondParRatio,
  propagateHumiditeSechageParRatio,
  formatHumiditePct,
  resolveMasseConditionnementStackRef,
  distributeMassesConditionnementPlisFromBanc,
  calcMasseCondPliProportionnelle,
  calcMasseSechPliProportionnelle,
} from "../utils/humiditeCalc";

/* ══════════════════════════════════════════
   HELPERS
══════════════════════════════════════════ */


function normalize(text) {
  return text
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}
function normalizePn(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, "")
    .replace(/-/g, "");
}

const normalizePlyKey = (value) =>
  String(value || "").trim().replace(/\s+/g, "").replace(/-/g, "");

const toNumberOrEmpty = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? n : "";
};

function toFloat(v) {
  if (!v && v !== 0) return 0;
  return Number(v.toString().replace(",", ".")) || 0;
}

const MASSE_SECH_FACTEUR_A = 0.969;
const MASSE_SECH_FACTEUR_B = -2.5;

/** masse_sech = 0.969 × masse_imprég − 2.5 (g) */
function resolveMasseImpregnation(value) {
  if (Array.isArray(value)) {
    const last = value[value.length - 1];
    return toFloat(last);
  }
  return toFloat(value);
}

function calcMasseSechage(masseImpreg) {
  const mi = resolveMasseImpregnation(masseImpreg);
  if (mi <= 0) return null;
  return Math.round((MASSE_SECH_FACTEUR_A * mi + MASSE_SECH_FACTEUR_B) * 100) / 100;
}

function calcHumiditePliSechageFromRatio(hImpPli, hSechStack, hImpStack) {
  if (
    hImpPli == null ||
    hSechStack == null ||
    hImpStack == null ||
    !Number.isFinite(hImpStack) ||
    hImpStack === 0
  ) {
    return "NA";
  }
  const h = hImpPli * (hSechStack / hImpStack);
  return formatHumiditePct(h, 4) ?? "NA";
}

function formatDuration(seconds) {
  if (seconds <= 0) return "00:00:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
}

function secondsUntil(isoString) {
  return Math.max(0, Math.floor((new Date(isoString) - Date.now()) / 1000));
}

function getPollingInterval(sessions) {
  let minRemaining = Infinity;
  for (const s of sessions) {
    if (s.endTimeSechage)         minRemaining = Math.min(minRemaining, secondsUntil(s.endTimeSechage));
    if (s.endTimeConditionnement) minRemaining = Math.min(minRemaining, secondsUntil(s.endTimeConditionnement));
  }
  if (minRemaining === Infinity) return 60 * 1000;
  if (minRemaining > 3600)       return 5 * 60 * 1000;
  if (minRemaining > 600)        return 60 * 1000;
  if (minRemaining > 60)         return 15 * 1000;
  return 5 * 1000;
}

function makeStack(stackId = "") {
  return { stackId };
}

function makeBloc() {
  return { active: false, duree: "", rangeeA: [], rangeeB: [] };
}

const LS_KEY = "tt_active_sessions";

/* ══════════════════════════════════════════
   HOOK — Polling adaptatif
══════════════════════════════════════════ */

function usePendingNotifications(activeSessions, onPending) {
  const notifiedRef = useRef(new Set());

  const check = useCallback(async () => {
    try {
      const res = await apiFetch("/api/traitement-thermique/pending");
      if (!res.ok) return;
      const data  = await res.json().catch(() => ({}));
      const items = data.items || [];

      for (const item of items) {
        const key = `${item.sessionTime}-${item.type}-${item.stackId}`;
        if (notifiedRef.current.has(key)) continue;
        notifiedRef.current.add(key);

        if ("Notification" in window && Notification.permission === "granted") {
          new Notification("🔔 Stack en attente de mesure !", {
            body: `${item.stackId} — ${item.type === "Sechage" ? "Séchage" : "Conditionnement"} Rangée ${item.rangee}`,
            icon: "/favicon.ico",
          });
        }
        onPending(item);
      }
    } catch (e) {
      console.error("Polling pending:", e);
    }
  }, [onPending]);

  useEffect(() => {
    check();
    let timeoutId;
    function schedule() {
      const interval = getPollingInterval(activeSessions);
      timeoutId = setTimeout(() => {
        check();
        schedule();
      }, interval);
    }
    schedule();
    return () => clearTimeout(timeoutId);
  }, [check, activeSessions]);
}

/* ══════════════════════════════════════════
   COMPOSANT — Minuteur Bloc
══════════════════════════════════════════ */

// ✅ onExpire toujours à jour via une ref
function BlocTimer({ type, numEnceinte, endTime, onExpire }) {
  const totalRef    = useRef(secondsUntil(endTime));
  const onExpireRef = useRef(onExpire);
  const [remaining, setRemaining] = useState(() => secondsUntil(endTime));
  const isUrgent = remaining <= 60;

  // Garder la ref à jour sans relancer le timer
  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    if (remaining <= 0) { onExpireRef.current(); return; }
    const id = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) { clearInterval(id); onExpireRef.current(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []); // ← stable grâce à la ref

  const pct = totalRef.current > 0
    ? Math.min(100, ((totalRef.current - remaining) / totalRef.current) * 100)
    : 100;

  return (
    <div className={`tt-timer-card ${isUrgent ? "tt-timer-urgent" : ""}`}>
      <div className="tt-timer-header">
        <span className="tt-timer-icon">
          {type === "Sechage" ? "🔥" : "💧"}
        </span>
        <div>
          <div className="tt-timer-title">
            {type === "Sechage" ? "SÉCHAGE" : "CONDITIONNEMENT"} — {numEnceinte}
          </div>
          <div className="tt-timer-sub">
            Fin prévue : <strong>{new Date(endTime).toLocaleTimeString("fr-FR")}</strong>
          </div>
        </div>
        <div className={`tt-timer-badge ${isUrgent ? "tt-timer-badge--urgent" : ""}`}>
          {isUrgent ? "⚠️ BIENTÔT" : "EN COURS"}
        </div>
      </div>

      <div className="tt-timer-display">{formatDuration(remaining)}</div>

      <div className="tt-timer-progress-wrap">
        <div className="tt-timer-progress-fill" style={{ width: `${pct}%` }} />
      </div>

      <div className="tt-timer-footer">
        <span className="tt-timer-info">
          ⏱️ Écoulé : <strong>{formatDuration(totalRef.current - remaining)}</strong>
        </span>
        <span className="tt-timer-info">
          ⏳ Restant : <strong>{formatDuration(remaining)}</strong>
        </span>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   COMPOSANT — Section Rangée (A ou B)
══════════════════════════════════════════ */

function RangeeSection({ type, rangee, stacks, stackPns, onAdd, onRemove }) {
  const [search,   setSearch]   = useState("");
  const [selected, setSelected] = useState("");
  const [showList, setShowList] = useState(false);
  const [showScan, setShowScan] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    function onOutside(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setShowList(false);
    }
    document.addEventListener("mousedown",  onOutside);
    document.addEventListener("touchstart", onOutside);
    return () => {
      document.removeEventListener("mousedown",  onOutside);
      document.removeEventListener("touchstart", onOutside);
    };
  }, []);

  const filtered = useMemo(() => {
    const s = normalize(search);
    return stackPns.filter((p) => !s || normalize(p).includes(s));
  }, [search, stackPns]);

  function handleAdd() {
    if (!selected) { alert("Merci de sélectionner un stack."); return; }
    onAdd(type, rangee, selected);
    setSelected("");
    setSearch("");
  }

  function handleScanResult(results) {
    if (!results?.length) return;
    const pn = (results[0].rawValue || "").trim();
    setSelected(pn);
    setSearch(pn);
    setShowList(false);
    setShowScan(false);
  }

  return (
    <div className="tt-rangee">
      <div className="tt-rangee-header">
        <span className="tt-rangee-badge">Rangée {rangee}</span>
        <span className="tt-rangee-count">
          {stacks.length}<span className="tt-rangee-max">/30</span>
        </span>
      </div>

      {stacks.length > 0 && (
        <div className="tt-stack-tags">
          {stacks.map((s, i) => (
            <div key={i} className="tt-stack-tag">
              <span className="tt-stack-tag-pn">{s.stackId}</span>
            </div>
          ))}
        </div>
      )}

      <div ref={boxRef} style={{ position: "relative" }}>
        <div className="tt-select-row">
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setShowList(true); }}
            onFocus={() => setShowList(true)}
            placeholder={`Stack Rangée ${rangee}…`}
            className="tt-select-input"
          />
          <button type="button" className="tt-scan-btn" onClick={() => setShowScan(true)}>
            📷
          </button>
        </div>

        <div className="tt-action-row">
          <button
            type="button"
            className="tt-btn-add"
            onClick={handleAdd}
            disabled={stacks.length >= 30}
          >
            + Ajouter
          </button>
          <button
            type="button"
            className="tt-btn-remove"
            onClick={() => onRemove(type, rangee)}
            disabled={stacks.length === 0}
          >
            − Retirer dernier
          </button>
        </div>

        {selected && (
          <div className="tt-selected-badge">
            <span>✔</span>
            <strong>{selected}</strong>
            <button
              type="button"
              className="tt-clear-btn"
              onClick={() => { setSelected(""); setSearch(""); }}
            >
              ✕
            </button>
          </div>
        )}

        {showList && filtered.length > 0 && (
          <div className="tt-dropdown" onMouseDown={(e) => e.preventDefault()}>
            {filtered.map((p) => (
              <div
                key={p}
                className="tt-dropdown-item"
                onClick={() => { setSelected(p); setSearch(p); setShowList(false); }}
              >
                {p}
              </div>
            ))}
          </div>
        )}
      </div>

      {showScan && (
        <div className="tt-modal-backdrop" onClick={() => setShowScan(false)}>
          <div className="tt-modal-scanner" onClick={(e) => e.stopPropagation()}>
            <div className="tt-modal-title">
              📷 Scanner — {type === "Sechage" ? "Séchage" : "Conditionnement"} Rangée {rangee}
            </div>
            <div style={{ borderRadius: 10, overflow: "hidden" }}>
              <Scanner
                onScan={handleScanResult}
                onError={console.error}
                constraints={{ facingMode: "environment" }}
              />
            </div>
            <button
              type="button"
              className="tt-btn-close-scanner"
              onClick={() => setShowScan(false)}
            >
              ✕ Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════
   COMPOSANT — Bloc Séchage / Conditionnement
══════════════════════════════════════════ */

function BlocSection({ type, bloc, onToggle, onSetDuree, onAddStack, onRemoveStack, stackPns }) {
  const label      = type === "Sechage" ? "🔥 Séchage" : "💧 Conditionnement";
  const stackCount = bloc.rangeeA.length + bloc.rangeeB.length;

  return (
    <div className={`tt-bloc-section ${bloc.active ? "tt-bloc-section--active" : ""}`}>
      <div className="tt-bloc-header" onClick={onToggle}>
        <span className="tt-bloc-title">{label}</span>
        <div className="tt-bloc-toggle-row">
          {bloc.active && (
            <span className="tt-bloc-count">
              {stackCount} stack{stackCount > 1 ? "s" : ""}
            </span>
          )}
          <div className={`tt-toggle-switch ${bloc.active ? "tt-toggle-switch--on" : ""}`}>
            <div className="tt-toggle-knob" />
          </div>
        </div>
      </div>

      {bloc.active && (
        <div className="tt-bloc-body">
          <div className="tt-duree-row">
            <span className="tt-section-label">Durée (h)</span>
            <input
              type="text"
              inputMode="decimal"
              value={bloc.duree}
              onChange={(e) => onSetDuree(e.target.value.replace(/[^0-9.,]/g, ""))}
              className="tt-num-input tt-duree-input"
              placeholder="Ex : 3"
            />
          </div>
          <div className="tt-rangees">
            {["A", "B"].map((rangee) => (
              <RangeeSection
                key={rangee}
                type={type}
                rangee={rangee}
                stacks={bloc[`rangee${rangee}`]}
                stackPns={stackPns}
                onAdd={onAddStack}
                onRemove={onRemoveStack}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════
   COMPOSANT — Modal Retrait anticipé
══════════════════════════════════════════ */

function RetraitModal({ session, onClose, onSuccess }) {
  const [selected, setSelected] = useState([]);
  const [sending,  setSending]  = useState(false);
  const [error,    setError]    = useState("");

  const stacks = useMemo(() => {
    const list = [];
    for (const type of ["Sechage", "Conditionnement"]) {
      const bloc = type === "Sechage" ? session.blocSechage : session.blocConditionnement;
      if (!bloc) continue;
      for (const rangee of ["A", "B"]) {
        for (const s of (bloc[`rangee${rangee}`] || [])) {
          if (!s.status || s.status === "en_cours") {
            list.push({ type, rangee, stackId: s.stackId });
          }
        }
      }
    }
    return list;
  }, [session]);

  function toggleStack(item) {
    const key = `${item.type}-${item.rangee}-${item.stackId}`;
    setSelected((prev) => {
      const exists = prev.find((s) => `${s.type}-${s.rangee}-${s.stackId}` === key);
      if (exists) return prev.filter((s) => `${s.type}-${s.rangee}-${s.stackId}` !== key);
      return [...prev, item];
    });
  }

  function isSelected(item) {
    return !!selected.find(
      (s) => s.type === item.type && s.rangee === item.rangee && s.stackId === item.stackId
    );
  }

  async function handleRetrait(e) {
    e.preventDefault();
    setError("");
    if (selected.length === 0) return setError("Merci de sélectionner au moins un stack.");
    try {
      setSending(true);
      const res = await apiFetch("/api/traitement-thermique/retrait", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ time: session.time, retraits: selected }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || "Erreur serveur");
      }
      onSuccess(selected);
    } catch (err) {
      setError("❌ " + (err.message || "Erreur d'envoi."));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="tt-modal-backdrop">
      <div className="tt-modal-retrait" onClick={(e) => e.stopPropagation()}>
        <div className="tt-compl-header tt-compl-header--retrait">
          <span>⚠️ Retrait anticipé de stacks</span>
          <button className="tt-compl-close" type="button" onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: "12px 16px", color: "#7ab8cc", fontSize: 12 }}>
          Sélectionne les stacks à retirer — horodatage automatique
        </div>

        <div className="tt-retrait-list">
          {stacks.length === 0 && (
            <div className="tt-retrait-empty">Aucun stack en cours dans cette session.</div>
          )}
          {["Sechage", "Conditionnement"].map((type) => {
            const typeStacks = stacks.filter((s) => s.type === type);
            if (!typeStacks.length) return null;
            return (
              <div key={type} className="tt-retrait-chariot">
                <div className="tt-retrait-chariot-label">
                  {type === "Sechage" ? "🔥 Séchage" : "💧 Conditionnement"}
                </div>
                {["A", "B"].map((rangee) => {
                  const rs = typeStacks.filter((s) => s.rangee === rangee);
                  if (!rs.length) return null;
                  return (
                    <div key={rangee} className="tt-retrait-rangee">
                      <div className="tt-retrait-rangee-label">Rangée {rangee}</div>
                      <div className="tt-retrait-stacks">
                        {rs.map((s, i) => (
                          <div
                            key={i}
                            className={`tt-retrait-stack ${isSelected(s) ? "tt-retrait-stack--selected" : ""}`}
                            onClick={() => toggleStack(s)}
                          >
                            <span className="tt-retrait-check">{isSelected(s) ? "✔" : "○"}</span>
                            <span>{s.stackId}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {selected.length > 0 && (
          <div className="tt-retrait-selection">
            {selected.length} stack{selected.length > 1 ? "s" : ""} sélectionné{selected.length > 1 ? "s" : ""}
          </div>
        )}

        {error && (
          <div className="tt-msg tt-msg-error" style={{ margin: "0 16px 10px" }}>
            {error}
          </div>
        )}

        <div className="tt-compl-footer">
          <button type="button" className="tt-btn-cancel" onClick={onClose}>✕ Annuler</button>
          <button
            type="button"
            disabled={sending || selected.length === 0}
            className="tt-btn-submit"
            onClick={handleRetrait}
          >
            {sending ? "⏳ Retrait…" : `⚠️ Confirmer retrait (${selected.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   COMPOSANT — Modal Complétion
══════════════════════════════════════════ */

/* ══════════════════════════════════════════
   COMPOSANT — Modal Complétion (validation individuelle)
══════════════════════════════════════════ */

/* ══════════════════════════════════════════
   COMPOSANT — Modal Complétion (validation individuelle)
══════════════════════════════════════════ */
function CompletionModal({
  pendingItems,
  sessionTime,
  loadingMasse,
  loadingAnhydre,
  loadingImpregPli,
  onClose,
  onSuccess,
  onStackValidated,
}) {
  const { ui } = useI18n();
  function stackMasseCondForRepartition(row) {
    const m = toFloat(row.masse);
    return m > 0 ? m : null;
  }

  function humiditeMerlinPli(p, row) {
    const normPn = normalizePn(p.plyPn);
    const hm =
      toFloat(p.humiditeMerlin) ||
      toFloat(row?.humiditeMerlinParPli?.[normPn]) ||
      toFloat(row?.humiditeMerlinParPli?.[p.plyPn]) ||
      toFloat(row?.humiditeMerlinParPli?.[normalizePlyKey(p.plyPn).toUpperCase()]);
    if (hm > 0 || (hm === 0 && Number.isFinite(hm))) return hm;
    return toFloat(row?.humiditeBancMoyenne);
  }

  function hMerlinStackMean(row, plisList) {
    const hmStack = toFloat(row?.humiditeBancMoyenne);
    if (Number.isFinite(hmStack) && hmStack > 0) return hmStack;
    const list = plisList || row?.plis || [];
    const vals = list
      .map((p) => humiditeMerlinPli(p, row))
      .filter((v) => Number.isFinite(v) && v >= 0);
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }

  function masseCondPliFromFormule(p, row) {
    const mCondStack = toFloat(row.masse);
    const mBancStack = toFloat(row.masseConditionnementRef);
    const mBancPli = masseBancPliForRepartition(p, row);
    return calcMasseCondPliProportionnelle(
      mBancPli,
      mCondStack,
      mBancStack
    );
  }

  function masseImpPliValue(p, row) {
    const normPn = normalizePn(p.plyPn);
    const fromPli = toFloat(p.masseImpregnation);
    if (fromPli > 0) return fromPli;
    const map = row.masseImpregnationParPli || {};
    const entry = map[normPn] ?? map[p.plyPn];
    if (entry && typeof entry === "object") {
      const m = toFloat(entry.masseImpregnation ?? entry.masse);
      if (m > 0) return m;
    }
    return toFloat(entry);
  }

  function masseSechPliFromFormule(p, row) {
    return calcMasseSechPliProportionnelle(
      masseImpPliValue(p, row),
      toFloat(row.masse),
      toFloat(row.masseSechageRef)
    );
  }

  function humiditeSechPliFromFormule(p, row) {
    const hImpPli = hImpForPli(p, row);
    const hImpStack = resolveHImpStackForSechage(row);
    let hSechStack = toFloat(row.humiditeApresSechage);
    if (!Number.isFinite(hSechStack) && toFloat(row.masse) > 0) {
      const hCalc = calcHumiditeSechageStack(
        hImpStack,
        row.masseSechageRef,
        row.masse,
        row.masseAnhydreStack
      );
      if (hCalc != null && Number.isFinite(hCalc)) {
        hSechStack = hCalc;
      }
    }
    if (
      hImpPli == null ||
      !Number.isFinite(hImpPli) ||
      hImpStack == null ||
      !(hImpStack > 0) ||
      !Number.isFinite(hSechStack)
    ) {
      return null;
    }
    const hProp = hImpPli * (hSechStack / hImpStack);
    return formatHumiditePct(hProp, 4);
  }

  function humiditeCondPliFromFormule(p, row) {
    const hmPli = humiditeMerlinPli(p, row);
    const hMerlinStack = hMerlinStackMean(row, row.plis);
    let hCondStack = toFloat(row.humiditeApresConditionnement);
    if (!Number.isFinite(hCondStack)) {
      hCondStack = calcHumiditeCondStack(
        row.masse,
        row.masseConditionnementRef,
        hMerlinStack
      );
    }
    const h = calcHumiditeCondPli(hmPli, hCondStack, hMerlinStack);
    return h != null ? formatHumiditePct(h, 4) : null;
  }

  function masseBancPliForRepartition(p, row) {
    const normPn = normalizePn(p.plyPn);
    const initMap = row?.masseInitialeParPli || {};
    const mi =
      toFloat(p.masseInitiale) ||
      toFloat(initMap[normPn]) ||
      toFloat(initMap[p.plyPn]) ||
      toFloat(initMap[normalizePlyKey(p.plyPn).toUpperCase()]);
    if (mi > 0) return mi;

    const anhMap = row?.masseAnhydreParPli || {};
    const ma =
      toFloat(p.masseAnhydre) ||
      toFloat(anhMap[normPn]) ||
      toFloat(anhMap[p.plyPn]) ||
      toFloat(anhMap[normalizePlyKey(p.plyPn).toUpperCase()]);
    const hm = humiditeMerlinPli(p, row);
    if (ma > 0 && Number.isFinite(hm)) {
      return ma * (1 + hm / 100);
    }
    return 0;
  }

  function distributeCondPlisFromStack(row) {
    const masseStack = toFloat(row.masse);
    const plisList = row.plis || [];
    if (!(masseStack > 0) || !plisList.length) return null;

    const massesBanc = plisList.map((p) => masseBancPliForRepartition(p, row));
    const totalBanc = massesBanc.reduce((a, b) => a + b, 0);
    const mBancStack = toFloat(row.masseConditionnementRef);

    if (!(masseStack > 0) || !(mBancStack > 0)) {
      return null;
    }
    if (!(totalBanc > 0) || massesBanc.some((m) => !(m > 0))) {
      return null;
    }
    return plisList.map((p) =>
      calcMasseCondPliProportionnelle(
        masseBancPliForRepartition(p, row),
        masseStack,
        mBancStack
      )
    );
  }

  function enrichConditionnementPlisMasses(row, plis) {
    if (row.type !== "Conditionnement" || !plis?.length) return plis;
    const stackM = stackMasseCondForRepartition(row);
    if (stackM == null) return plis;
    const distributed = distributeCondPlisFromStack(row);
    if (!distributed) return plis;
    return plis.map((p, idx) => ({
      ...p,
      masseConditionnement:
        p.masseConditionnement ??
        (distributed[idx] != null && Number.isFinite(distributed[idx])
          ? distributed[idx]
          : null),
    }));
  }

  function buildPliRows(row, keepExistingPlis = []) {
  const existingByPosition = new Map(
    (keepExistingPlis || []).map((p) => [Number(p.position), p])
  );

  const built = Array.from({ length: row.nbPlis || 0 }, (_, idx) => {
    const position = idx + 1;
    const info = row.plisInfo?.find((p) => Number(p.position) === position);
    const existing = existingByPosition.get(position);

    const plyPn =
      info?.plyPn ||
      existing?.plyPn ||
      `Pli ${String(position).padStart(2, "0")}`;

    const normPn = normalizePn(plyPn);

    return {
      position,
      plyPn,
      masse: existing?.masse ?? "",
      masseAnhydre:
        row.masseAnhydreParPli?.[plyPn] ??
        row.masseAnhydreParPli?.[normPn] ??
        existing?.masseAnhydre ??
        null,
      masseImpregnation:
        row.masseImpregnationParPli?.[plyPn]?.masseImpregnation ??
        row.masseImpregnationParPli?.[normPn]?.masseImpregnation ??
        existing?.masseImpregnation ??
        null,
      masseConditionnement:
        row.mesureParPli || row.type !== "Conditionnement"
          ? row.masseConditionnementParPli?.[plyPn] ??
            row.masseConditionnementParPli?.[normPn] ??
            existing?.masseConditionnement ??
            null
          : existing?.masseConditionnement ?? null,
      masseInitiale:
        row.masseInitialeParPli?.[plyPn] ??
        row.masseInitialeParPli?.[normPn] ??
        existing?.masseInitiale ??
        null,
      humiditeMerlin: (() => {
        const hm =
          row.humiditeMerlinParPli?.[normPn] ??
          row.humiditeMerlinParPli?.[plyPn] ??
          row.humiditeMerlinParPli?.[normalizePlyKey(plyPn).toUpperCase()] ??
          existing?.humiditeMerlin;
        if (hm != null && hm !== "") return hm;
        return row.humiditeBancMoyenne ?? null;
      })(),
      masseSechCalc: existing?.masseSechCalc ?? null,
      humidite:
        existing?.humidite ??
        (row.humiditeCondParPosition?.[String(position)] != null
          ? formatHumiditePct(row.humiditeCondParPosition[String(position)], 4)
          : null) ??
        (row.humiditeCondParPli?.[normPn] != null
          ? formatHumiditePct(row.humiditeCondParPli[normPn], 4)
          : null),
      perteMassique: existing?.perteMassique ?? null,
      masseRepartStack: existing?.masseRepartStack ?? null,
    };
  });
  return enrichConditionnementPlisMasses(row, built);
}

  function hydrateItem(item, existing = null) {
    const base = {
      ...item,
      masse: existing?.masse ?? item.masse ?? "",
      mesureParPli: existing?.mesureParPli ?? item.mesureParPli ?? false,
      plis: existing?.plis ?? item.plis ?? [],
      plisInfo:
        item.plisInfo?.length
          ? item.plisInfo
          : existing?.plisInfo?.length
            ? existing.plisInfo
            : [],
      nbPlis:
        Math.max(
          Number(item.nbPlis) || 0,
          Number(existing?.nbPlis) || 0,
          (item.plisInfo || existing?.plisInfo || []).length
        ) || 0,
      masseSechageRef: item.masseImpregnation ?? existing?.masseSechageRef ?? null,
      masseConditionnementRef: item.masseInit ?? existing?.masseConditionnementRef ?? null,
      masseConditionnementStack:
        item.masseConditionnementStack ?? existing?.masseConditionnementStack ?? null,
      masseAnhydreStack: item.masseAnhydreStack ?? existing?.masseAnhydreStack ?? null,
      masseAnhydreParPli: item.masseAnhydreParPli ?? existing?.masseAnhydreParPli ?? {},
      masseImpregnationParPli:
        item.masseImpregnationParPli ?? existing?.masseImpregnationParPli ?? {},
      masseConditionnementParPli:
        item.masseConditionnementParPli ?? existing?.masseConditionnementParPli ?? {},
      masseInitialeParPli:
        item.masseInitialeParPli ?? existing?.masseInitialeParPli ?? {},
      humiditeMerlinParPli:
        item.humiditeMerlinParPli ?? existing?.humiditeMerlinParPli ?? {},
      humiditeBancMoyenne:
        item.humiditeBancMoyenne ?? existing?.humiditeBancMoyenne ?? null,
      humiditeApresImpregnationStack:
        item.humiditeApresImpregnationStack ??
        item.humiditeApresImpregnation ??
        existing?.humiditeApresImpregnationStack ??
        null,
      referencesReady: item.referencesReady ?? existing?.referencesReady ?? false,
      plisReferencesReady:
        item.plisReferencesReady ?? existing?.plisReferencesReady ?? false,
      plisReferencesLoading: existing?.plisReferencesLoading ?? false,
      perteMassiqueConditionnement:
        existing?.perteMassiqueConditionnement ??
        item.perteMassiqueConditionnement ??
        null,
      humiditeApresConditionnement:
        existing?.humiditeApresConditionnement ??
        item.humiditeApresConditionnement ??
        (item.humiditeApresConditionnementStack != null
          ? formatHumiditePct(item.humiditeApresConditionnementStack, 4)
          : null),
      humiditeApresSechage:
        existing?.humiditeApresSechage ?? item.humiditeApresSechage ?? null,
      humiditeParPli: existing?.humiditeParPli ?? item.humiditeParPli ?? [],
      humiditeCondParPosition:
        item.humiditeCondParPosition ?? existing?.humiditeCondParPosition ?? {},
      humiditeCondParPli:
        item.humiditeCondParPli ?? existing?.humiditeCondParPli ?? {},
      perteMassiqueSechage:
        existing?.perteMassiqueSechage ?? item.perteMassiqueSechage ?? null,
      validated: existing?.validated ?? item.validated ?? false,
      validating: existing?.validating ?? item.validating ?? false,
      errorMsg: existing?.errorMsg ?? item.errorMsg ?? "",
    };

    if (base.nbPlis > 0 && base.plisReferencesReady) {
      if (base.type === "Conditionnement" || base.type === "Sechage") {
        base.plis = buildPliRows(base, existing?.plis ?? item.plis ?? []);
        if (!base.mesureParPli && base.plis.length > 0) {
          let hydrated = applyAutoMassesToRow(base);
          if (toFloat(hydrated.masse) > 0) {
            const hm = hMerlinStackMean(hydrated, hydrated.plis);
            const mi = toFloat(hydrated.masseConditionnementRef);
            const h = calcHumiditeCondStack(hydrated.masse, mi, hm);
            if (h != null) {
              hydrated = {
                ...hydrated,
                humiditeApresConditionnement: formatHumiditePct(h, 4),
              };
              hydrated = applyAutoMassesToRow(hydrated);
            }
          }
          return hydrated;
        }
      } else if (base.mesureParPli) {
        base.plis = buildPliRows(base, existing?.plis ?? item.plis ?? []);
      }
    }

    return base;
  }

  function isPlyAutoMode(row) {
    return (
      !row?.mesureParPli &&
      (row?.type === "Conditionnement" || row?.type === "Sechage")
    );
  }

  const [rows, setRows] = useState(() =>
    pendingItems.map((item) => hydrateItem(item))
  );

  function resolveHImpStackForSechage(row) {
    const raw = row.humiditeApresImpregnationStack;
    if (raw != null && raw !== "") {
      const h = parseFloat(String(raw).replace(",", "."));
      if (Number.isFinite(h)) return h;
    }
    const plis = row.plis || [];
    if (!plis.length) return null;
    const vals = plis
      .map((p) => hImpForPli(p, row))
      .filter((v) => v != null && Number.isFinite(v));
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }

  function applySechageStackHumidite(row) {
    if (row.type !== "Sechage") return row;
    const masseImpregnation = toFloat(row.masseSechageRef);
    const masseSechage = toFloat(row.masse);
    const masseAnhydre = toFloat(row.masseAnhydreStack);
    const hImpStack = resolveHImpStackForSechage(row);
    if (
      !(masseImpregnation > 0 && masseSechage > 0 && masseAnhydre > 0) ||
      hImpStack == null ||
      !Number.isFinite(hImpStack)
    ) {
      return row;
    }
    const h = calcHumiditeSechageStack(
      hImpStack,
      masseImpregnation,
      masseSechage,
      masseAnhydre
    );
    return {
      ...row,
      perteMassiqueSechage: calcPerteMassique(row.masse, row.masseSechageRef),
      humiditeApresSechage: h != null ? formatHumiditePct(h, 4) : null,
    };
  }

  useEffect(() => {
    setRows((prev) =>
      pendingItems.map((item) => {
        const existing = prev.find(
          (r) =>
            r.stackId === item.stackId &&
            r.type === item.type &&
            r.rangee === item.rangee
        );
        const row = hydrateItem(item, existing);
        return applySechageStackHumidite(row);
      })
    );
  }, [pendingItems]);

  const [globalError, setGlobalError] = useState("");

  const allValidated = rows.length > 0 && rows.every((r) => r.validated);
  const validatedCount = rows.filter((r) => r.validated).length;

  useEffect(() => {
    if (allValidated) {
      const t = setTimeout(() => onSuccess(), 800);
      return () => clearTimeout(t);
    }
  }, [allValidated, onSuccess]);

  function calcPerteMassique(masseFinale, masseReference) {
    const mf = toFloat(masseFinale);
    const mr = toFloat(masseReference);
    if (mf > 0 && mr > 0) {
      return (((mf - mr) / mr) * 100).toFixed(4);
    }
    return null;
  }

  function hImpForPli(pli, row) {
    let hCond = pli.hCondPli ?? pli.humiditeCondPli;
    const hBanc = toFloat(row.humiditeBancMoyenne);
    const hStackC = toFloat(row.humiditeApresConditionnement);
    if (
      (hCond == null || hCond === "") &&
      hBanc > 0 &&
      hStackC != null &&
      Number.isFinite(hStackC) &&
      pli.humiditeMerlin != null
    ) {
      hCond = propagateHumiditeCondParRatio(
        pli.humiditeMerlin,
        hStackC,
        hBanc
      );
    }
    return calcHumiditeImpPli(
      pli.masseImpregnation,
      pli.masseConditionnement,
      pli.masseAnhydre,
      hCond
    );
  }

  function applyAutoMassesToRow(row) {
    if (!isPlyAutoMode(row)) {
      return row;
    }

    if (row.type === "Conditionnement") {
      const plisList = row.plis || [];
      const hBanc = hMerlinStackMean(row, plisList);
      const masseStack = toFloat(row.masse);
      const hStackCondRaw = toFloat(row.humiditeApresConditionnement);
      const hStackCond =
        hStackCondRaw != null && Number.isFinite(hStackCondRaw)
          ? hStackCondRaw
          : calcHumiditeCondStack(
              masseStack,
              row.masseConditionnementRef,
              hBanc
            );
      const distributed = distributeCondPlisFromStack(row);
      const canRepartStack =
        masseStack > 0 &&
        Array.isArray(distributed) &&
        distributed.length === plisList.length &&
        distributed.every((m) => m != null && Number.isFinite(m) && m > 0);

      const plis = plisList.map((p, idx) => {
        const repartM =
          distributed && distributed[idx] != null && Number.isFinite(distributed[idx])
            ? distributed[idx]
            : null;

        if (repartM == null) {
          return {
            ...p,
            masseRepartStack: null,
            masseConditionnement:
              row.masseConditionnementParPli?.[normalizePn(p.plyPn)] ??
              row.masseConditionnementParPli?.[p.plyPn] ??
              null,
            masse: "",
            perteMassique: null,
            humidite: null,
          };
        }

        const masseStr = String(repartM);
        let humidite = null;
        const hmPli = humiditeMerlinPli(p, row);
        if (
          hStackCond != null &&
          Number.isFinite(hStackCond) &&
          hBanc != null &&
          hBanc > 0 &&
          Number.isFinite(hmPli)
        ) {
          const hProp = calcHumiditeCondPli(hmPli, hStackCond, hBanc);
          humidite = hProp != null ? formatHumiditePct(hProp, 4) : null;
        }
        return {
          ...p,
          masseRepartStack: repartM,
          masseConditionnement: repartM,
          masse: masseStr,
          perteMassique: calcPerteMassique(masseStr, p.masseInitiale),
          humidite,
        };
      });

      let humiditeApresConditionnement = row.humiditeApresConditionnement;
      if (
        (humiditeApresConditionnement == null ||
          humiditeApresConditionnement === "") &&
        hStackCond != null &&
        Number.isFinite(hStackCond)
      ) {
        humiditeApresConditionnement = formatHumiditePct(hStackCond, 4);
      }

      return {
        ...row,
        plis,
        humiditeApresConditionnement,
        repartitionStackActive: canRepartStack,
        perteMassiqueConditionnement: row.masse
          ? calcPerteMassique(row.masse, row.masseConditionnementRef)
          : row.perteMassiqueConditionnement,
      };
    }

    if (row.type === "Sechage") {
      const hydrated = applySechageStackHumidite(row);
      const mSechStack = toFloat(hydrated.masse);
      const mImpStack = toFloat(hydrated.masseSechageRef);

      const plis = (hydrated.plis || []).map((p) => {
        const mSech = masseSechPliFromFormule(p, hydrated);
        if (mSech == null || !Number.isFinite(mSech)) {
          return { ...p, masseSechCalc: null, masse: "", humidite: null };
        }
        const humidite = humiditeSechPliFromFormule(p, hydrated);
        return {
          ...p,
          masseSechCalc: mSech,
          masse: String(mSech),
          humidite,
        };
      });

      return {
        ...hydrated,
        plis,
        perteMassiqueSechage: hydrated.masse
          ? calcPerteMassique(hydrated.masse, hydrated.masseSechageRef)
          : hydrated.perteMassiqueSechage,
      };
    }

    return row;
  }

  function setField(i, field, value) {
    setRows((prev) => {
      const next = [...prev];
      let row = { ...next[i], [field]: value };

      if (field !== "masse") {
        next[i] = row;
        return next;
      }

      const masseAnhydre = toFloat(row.masseAnhydreStack);

      row.perteMassiqueConditionnement = null;
      row.perteMassiqueSechage = null;
      row.humiditeApresConditionnement = null;
      row.humiditeApresSechage = null;

     if (row.type === "Conditionnement") {
  row.perteMassiqueConditionnement = calcPerteMassique(
    value,
    row.masseConditionnementRef
  );
  const mi = toFloat(row.masseConditionnementRef);
  const ma = toFloat(value);
  const hm = hMerlinStackMean(row, row.plis);
  const hCond =
    ma > 0 && mi > 0 && hm != null && Number.isFinite(hm)
      ? calcHumiditeCondStack(ma, mi, hm)
      : null;
  row.humiditeApresConditionnement = hCond != null ? formatHumiditePct(hCond, 4) : null;
  if (isPlyAutoMode(row) && row.plis?.length) {
    row = applyAutoMassesToRow(row);
  } else if (row.plis?.length) {
    row.plis = enrichConditionnementPlisMasses(row, row.plis);
  }
}

     if (row.type === "Sechage") {
  row.masse = value;
  row = applySechageStackHumidite(row);
  if (isPlyAutoMode(row) && row.plis?.length) {
    row.plis = applyAutoMassesToRow(row).plis;
  }
}
      next[i] = row;
      return next;
    });
  }

  async function toggleParPli(i) {
    const row = rows[i];
    if (!row) return;

    const isAutoPlyType =
      row.type === "Conditionnement" || row.type === "Sechage";

    if (row.mesureParPli) {
      setRows((prev) => {
        const next = [...prev];
        const current = { ...next[i] };

        if (isAutoPlyType) {
          current.mesureParPli = false;
          current.errorMsg = "";
          if (current.nbPlis > 0 && current.plisReferencesReady) {
            current.plis = buildPliRows(current, current.plis);
            next[i] = applyAutoMassesToRow(current);
          } else {
            next[i] = current;
          }
        } else {
          next[i] = {
            ...current,
            mesureParPli: false,
            plis: [],
          };
        }
        return next;
      });
      return;
    }

    if (!row.plisReferencesReady) {
      setRows((prev) => {
        const next = [...prev];
        next[i] = {
          ...next[i],
          plisReferencesLoading: true,
          errorMsg: "",
        };
        return next;
      });

      try {
        const res = await apiFetch(
          `/api/traitement-thermique/references-completion?stackIds=${encodeURIComponent(
            row.stackId
          )}`
        );

        const data = res.ok ? await res.json().catch(() => ({})) : {};
        const ref = data.items?.[row.stackId] || {};

        setRows((prev) => {
          const next = [...prev];
          const current = { ...next[i] };

          current.masseInit = ref.masseInit ?? current.masseInit;
          current.masseImpregnation = ref.masseImpregnation ?? current.masseImpregnation;
          current.masseConditionnementStack =
            ref.masseConditionnementStack ?? current.masseConditionnementStack;
          current.masseSechageRef = current.masseImpregnation ?? current.masseSechageRef;
          current.masseConditionnementRef =
            current.masseInit ?? current.masseConditionnementRef;
          current.masseAnhydreParPli = ref.masseAnhydreParPli ?? {};
          current.masseImpregnationParPli = ref.masseImpregnationParPli ?? {};
          current.masseConditionnementParPli = ref.masseConditionnementParPli ?? {};
          current.masseInitialeParPli = ref.masseInitialeParPli ?? {};
          current.humiditeMerlinParPli = ref.humiditeMerlinParPli ?? {};
          current.humiditeBancMoyenne = ref.humiditeBancMoyenne ?? null;
          current.humiditeApresImpregnationStack =
            ref.humiditeApresImpregnation ?? current.humiditeApresImpregnationStack;
          if (ref.humiditeApresConditionnementStack != null) {
            current.humiditeApresConditionnementStack =
              ref.humiditeApresConditionnementStack;
            current.humiditeApresConditionnement = formatHumiditePct(
              ref.humiditeApresConditionnementStack,
              4
            );
          }
          if (ref.plisInfo?.length) {
            current.plisInfo = ref.plisInfo;
            current.nbPlis = Math.max(
              Number(current.nbPlis) || 0,
              ref.plisInfo.length
            );
          }
          current.humiditeCondParPosition = ref.humiditeCondParPosition ?? {};
          current.humiditeCondParPli = ref.humiditeCondParPli ?? {};
          current.masseAnhydreStack =
            ref.masseAnhydreStack ?? current.masseAnhydreStack;
          current.plisReferencesReady = true;
          current.plisReferencesLoading = false;
          current.errorMsg = "";

          if (isAutoPlyType) {
            current.mesureParPli = true;
            current.plis = buildPliRows(current, []).map((p) => ({
              ...p,
              masse: "",
              perteMassique: null,
              humidite: null,
            }));
            next[i] = applySechageStackHumidite(current);
          } else {
            current.mesureParPli = true;
            current.plis = buildPliRows(current, current.plis);
            next[i] = applySechageStackHumidite(current);
          }
          return next;
        });
      } catch (err) {
        setRows((prev) => {
          const next = [...prev];
          next[i] = {
            ...next[i],
            plisReferencesLoading: false,
            errorMsg: "❌ Impossible de charger les références par pli.",
          };
          return next;
        });
      }

      return;
    }

    setRows((prev) => {
      const next = [...prev];
      const current = { ...next[i] };

      current.mesureParPli = true;
      current.errorMsg = "";

      if (isAutoPlyType) {
        current.plis = buildPliRows(current, []).map((p) => ({
          ...p,
          masse: "",
          perteMassique: null,
          humidite: null,
        }));
        next[i] = current;
      } else {
        current.plis = buildPliRows(current, current.plis);
        next[i] = current;
      }
      return next;
    });
  }

function setPliField(rowIdx, pliIdx, field, value) {
  setRows((prev) => {
    const next = [...prev];
    const row = { ...next[rowIdx] };
    const plis = [...(row.plis || [])];

    if (!plis[pliIdx]) {
      return prev;
    }

    const sanitizedValue =
      field === "masse"
        ? String(value ?? "").replace(/[^0-9.,]/g, "")
        : value;

    if (field === "masse" && isPlyAutoMode(row)) {
      return prev;
    }

    const currentPli = {
      ...plis[pliIdx],
      [field]: sanitizedValue,
    };

  if (field === "masse") {
  const massePli = toFloat(currentPli.masse);
  const masseImpregnation = toFloat(currentPli.masseImpregnation);
  const masseConditionnement = toFloat(currentPli.masseConditionnement);

  if (row.type === "Conditionnement") {
    currentPli.perteMassique = calcPerteMassique(
      currentPli.masse,
      currentPli.masseInitiale
    );
    if (massePli > 0) {
      currentPli.masseConditionnement = massePli;
    }
    const hStackCond = toFloat(row.humiditeApresConditionnement);
    const hBanc = toFloat(row.humiditeBancMoyenne);
    const hCondStackEff =
      hStackCond != null && Number.isFinite(hStackCond)
        ? hStackCond
        : calcHumiditeCondStack(row.masse, row.masseConditionnementRef, hBanc);
    if (
      hCondStackEff != null &&
      Number.isFinite(hCondStackEff) &&
      hBanc > 0 &&
      currentPli.humiditeMerlin != null
    ) {
      const hProp = calcHumiditeCondPli(
        currentPli.humiditeMerlin,
        hCondStackEff,
        hBanc
      );
      currentPli.humidite =
        hProp != null ? formatHumiditePct(hProp, 4) : null;
    } else {
      currentPli.humidite = null;
    }
  } else {
    currentPli.perteMassique = null;
    const hImp = hImpForPli(currentPli, row);
    const hStack = toFloat(row.humiditeApresSechage);
    if (hImp != null && hStack > 0 && row.plis?.length) {
      const hImpMap = {};
      row.plis.forEach((p, i) => {
        if (i === pliIdx) hImpMap[i] = hImp;
        else {
          const hi = hImpForPli(p, row);
          if (hi != null) hImpMap[i] = hi;
        }
      });
      const hImpStack = toFloat(row.humiditeApresImpregnationStack);
      const propagated = propagateHumiditeSechageParRatio(
        hStack,
        hImpMap,
        hImpStack
      );
      const hSech = propagated[pliIdx];
      currentPli.humidite =
        hSech != null
          ? formatHumiditePct(hSech, 4)
          : calcHumiditePliSechageFromRatio(hImp, hStack, hImpStack);
    } else {
      currentPli.humidite = massePli <= 0 ? null : "NA";
    }
  }
}

    plis[pliIdx] = currentPli;
    row.plis = plis;

    next[rowIdx] = row;

    return next;
  });
}

 async function handleValidateSingle(i) {
  // Une validation à la fois : chaque envoi réécrit toute la session côté serveur
  if (rows.some((x) => x.validating)) return;
  setGlobalError("");
  const r = rows[i];

  if (!r.masse) {
    setRows((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], errorMsg: "Masse manquante." };
      return next;
    });
    return;
  }
  if (!(toFloat(r.masse) > 0)) {
    setRows((prev) => {
      const next = [...prev];
      next[i] = {
        ...next[i],
        errorMsg:
          "Masse invalide : elle doit être supérieure à 0 g. Si ce stack n'a pas été traité, utilisez « Annuler ce stack ».",
      };
      return next;
    });
    return;
  }

  if (
    r.type === "Sechage" &&
    r.plis.length > 0 &&
    isPlyAutoMode(r)
  ) {
    for (let j = 0; j < r.plis.length; j++) {
      if (!r.plis[j].masse) {
        setRows((prev) => {
          const next = [...prev];
          next[i] = {
            ...next[i],
            errorMsg: `Pli ${j + 1} : masse manquante (calcul auto impossible).`,
          };
          return next;
        });
        return;
      }
    }
  } else if (r.mesureParPli) {
    for (let j = 0; j < r.plis.length; j++) {
      if (!(toFloat(r.plis[j].masse) > 0)) {
        setRows((prev) => {
          const next = [...prev];
          next[i] = {
            ...next[i],
            errorMsg: `Pli ${j + 1} : masse manquante.`,
          };
          return next;
        });
        return;
      }
    }
  }

  setRows((prev) => {
    const next = [...prev];
    next[i] = { ...next[i], validating: true, errorMsg: "" };
    return next;
  });

  try {
    const res = await apiFetch("/api/traitement-thermique/complete", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        time: r.sessionTime,
        completions: [
          {
            type: r.type,
            rangee: r.rangee,
            stackId: r.stackId,
            masse: toFloat(r.masse),
            mesureParPli: r.mesureParPli,
            plis: r.mesureParPli
              ? r.plis.map((p) => ({
                  position: p.position,
                  plyPn: p.plyPn,
                  masse: toFloat(p.masse),
                }))
              : isPlyAutoMode(r) && r.type === "Conditionnement"
                ? []
                : r.type === "Conditionnement" || r.type === "Sechage"
                  ? r.plis.map((p) => ({
                      position: p.position,
                      plyPn: p.plyPn,
                      masse: toFloat(p.masse),
                    }))
                  : [],
          },
        ],
      }),
    });

    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.message || "Erreur serveur");
    }

    const d = await res.json().catch(() => ({}));
    const humData = d.humiditeParStack?.[r.stackId];

    const humSection =
      r.type === "Conditionnement"
        ? humData?.conditionnement
        : r.type === "Sechage"
          ? humData?.sechage
          : null;

    setRows((prev) => {
      const next = [...prev];
      const current = { ...next[i] };

      const returnedPlis = Array.isArray(humSection?.plis) ? humSection.plis : [];

      const updatedPlis = (current.plis || []).map((pli, idx) => ({
        ...pli,
        humidite:
          returnedPlis[idx]?.humidite ??
          pli.humidite ??
          null,
      }));

      next[i] = {
        ...current,
        validated: true,
        validating: false,
        errorMsg: "",
      humiditeApresConditionnement: null,
        humiditeParPli:
          returnedPlis.length > 0
            ? returnedPlis.map((p) => p.humidite ?? null)
            : current.humiditeParPli,
        humiditeApresSechage:
          r.type === "Sechage"
            ? humSection?.stack ?? current.humiditeApresSechage
            : current.humiditeApresSechage,
        plis: updatedPlis,
      };

      return next;
    });

    onStackValidated(sessionTime, r.stackId, r.type);
  } catch (err) {
    setRows((prev) => {
      const next = [...prev];
      next[i] = {
        ...next[i],
        validating: false,
        errorMsg: "❌ " + (err.message || "Erreur d'envoi."),
      };
      return next;
    });
  }
}

 /* ── Annulation d'un stack mis par erreur dans la session ── */
 function setCancelField(i, patch) {
  setRows((prev) => {
    const next = [...prev];
    next[i] = { ...next[i], ...patch };
    return next;
  });
 }

 async function handleCancelSingle(i) {
  if (rows.some((x) => x.validating)) return;
  const r = rows[i];
  if (!r.cancelMotif) {
    setCancelField(i, { errorMsg: "Choisissez un motif d'annulation." });
    return;
  }
  setCancelField(i, { validating: true, errorMsg: "" });
  try {
    const res = await apiFetch("/api/traitement-thermique/annuler-stack", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        time: r.sessionTime,
        type: r.type,
        rangee: r.rangee,
        stackId: r.stackId,
        motif: r.cancelMotif,
      }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || d.status !== "success") {
      throw new Error(d.message || "Erreur serveur");
    }
    setCancelField(i, { validating: false, validated: true, cancelled: true, cancelOpen: false });
    onStackValidated(r.sessionTime, r.stackId, r.type);
  } catch (err) {
    setCancelField(i, {
      validating: false,
      errorMsg: "❌ " + (err.message || "Erreur d'annulation."),
    });
  }
 }

  return (
    <div className="tt-modal-backdrop">
      <div className="tt-modal-completion" onClick={(e) => e.stopPropagation()}>
        <div className="tt-compl-header">
          <span>
            Saisie des masses — {validatedCount}/{rows.length} validé
            {validatedCount > 1 ? "s" : ""}
          </span>
          <button className="tt-compl-close" type="button" onClick={onClose}>
            ×
          </button>
        </div>

        {(loadingMasse || loadingAnhydre || loadingImpregPli) && (
          <div className="tt-compl-mimp-bar">
            <div className="tt-compl-mimp-loading">
              Chargement des masses de référence...
            </div>
          </div>
        )}

        <div style={{ height: 4, background: "#1a4a5a" }}>
          <div
            style={{
              height: "100%",
              width: `${rows.length > 0 ? (validatedCount / rows.length) * 100 : 0}%`,
              background: "linear-gradient(90deg, #00B050, #E6FF3E)",
              transition: "width 0.4s ease",
            }}
          />
        </div>

        <div className="tt-compl-scroll">
          {rows.map((r, i) => (
            <div
              key={`${r.sessionTime || sessionTime}-${r.type}-${r.rangee}-${r.stackId}`}
              className="tt-compl-row"
              style={r.validated ? { opacity: 0.6, border: "1px solid #00B050" } : {}}
            >
              <div className="tt-compl-row-header">
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span className="tt-compl-stack-id">{r.stackId}</span>

                  {r.referencesReady ? (
                    <span className="tt-ref-badge tt-ref-badge--ok">Références OK</span>
                  ) : (
                    <span className="tt-ref-badge tt-ref-badge--loading">{ui("Chargement...")}</span>
                  )}
                </div>

                <span className="tt-compl-stack-meta">
                  {r.type === "Sechage" ? "Séchage" : "Conditionnement"} — Rangée {r.rangee}
                  {r.numEnceinte ? ` • ${r.numEnceinte}` : ""}
                </span>

                {r.validated && (
                  <span
                    style={{
                      marginLeft: "auto",
                      background: "#002810",
                      color: "#00B050",
                      border: "1px solid #00B050",
                      borderRadius: 6,
                      padding: "2px 10px",
                      fontSize: 11,
                      fontWeight: 800,
                    }}
                  >
                    {r.cancelled ? "🚫 Annulé" : "✅ Validé"}
                  </span>
                )}
              </div>

              {r.cancelled ? (
                <div style={{ color: "#5a8a99", fontSize: 12, padding: "4px 0" }}>
                  Stack annulé : <strong>{r.cancelMotif}</strong>. Il sort de la liste « à mesurer » et n'entre pas dans les calculs.
                </div>
              ) : r.validated ? (
                <div style={{ color: "#5a8a99", fontSize: 12, padding: "4px 0" }}>
                  Masse enregistrée : <strong style={{ color: "#E6FF3E" }}>{r.masse} g</strong>

                  {r.type === "Conditionnement" && (
  <>
    {r.masseAnhydreStack != null && toFloat(r.masseAnhydreStack) > 0 && (
      <>
        {" "}· Masse anhydre :{" "}
        <strong style={{ color: "#7ab8cc" }}>
          {parseFloat(r.masseAnhydreStack).toFixed(2)} g
        </strong>
      </>
    )}
    {r.humiditeApresConditionnement != null && (
      <>
        {" "}· Humidité après conditionnement :{" "}
        <strong style={{ color: "#00B050" }}>
          {parseFloat(r.humiditeApresConditionnement).toFixed(4)} %
        </strong>
      </>
    )}
  </>
)}



                  {r.type === "Sechage" && r.perteMassiqueSechage != null && (
                    <>
                      {" "}· Perte :{" "}
                      <strong
                        style={{
                          color:
                            parseFloat(r.perteMassiqueSechage) >= 0
                              ? "#00B050"
                              : "#C00000",
                        }}
                      >
                        {r.perteMassiqueSechage} %
                      </strong>
                    </>
                  )}

                {r.type === "Sechage" && r.humiditeApresSechage != null && (
  <>
    {" "}· Humidité après séchage{" "}
    <strong
      style={{
        color:
          parseFloat(r.humiditeApresSechage) < 3 ||
          parseFloat(r.humiditeApresSechage) > 10
            ? "#C00000"
            : "#00B050",
      }}
    >
      {parseFloat(r.humiditeApresSechage).toFixed(4)} %
    </strong>
  </>
)}

                </div>
              ) : (
                <>
                 {r.type === "Conditionnement" &&
  r.masseAnhydreStack != null &&
  toFloat(r.masseAnhydreStack) > 0 && (
  <div className="tt-compl-mimp-bar">
    <div className="tt-compl-mimp-info">
      <span className="tt-compl-mimp-item">
        Masse anhydre{" "}
        <strong>{parseFloat(r.masseAnhydreStack).toFixed(2)} g</strong>
      </span>
      <span className="tt-compl-mimp-item">
        Humidité stack{" "}
        <strong>
          {r.humiditeApresConditionnement != null
            ? `${parseFloat(r.humiditeApresConditionnement).toFixed(4)} %`
            : "--"}
        </strong>
      </span>
    </div>
  </div>
)}

                  {r.type === "Sechage" && r.masseSechageRef != null && (
                    <div className="tt-compl-mimp-bar">
                      <div className="tt-compl-mimp-info">
                        <span className="tt-compl-mimp-item">
                          Masse imprégnation <strong>{r.masseSechageRef} g</strong>
                        </span>
                        {resolveMasseConditionnementStackRef(r) != null && (
                          <span className="tt-compl-mimp-item">
                            Masse conditionnement{" "}
                            <strong>
                              {parseFloat(resolveMasseConditionnementStackRef(r)).toFixed(2)} g
                            </strong>
                          </span>
                        )}
                        <span className="tt-compl-mimp-item">
                          Perte massique <strong>{r.perteMassiqueSechage ?? "--"} %</strong>
                        </span>
                        <span className="tt-compl-mimp-item">
                          Humidité après séchage{" "}
                        <strong
  style={{
    color:
      r.humiditeApresSechage != null
        ? parseFloat(r.humiditeApresSechage) < 3 ||
          parseFloat(r.humiditeApresSechage) > 10
          ? "#C00000"
          : "#00B050"
        : "inherit",
  }}
>
  {r.humiditeApresSechage != null
    ? `${parseFloat(r.humiditeApresSechage).toFixed(4)} %`
    : "--"}
</strong>
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="tt-compl-fields">
                    <div className="tt-compl-field">
                      <label className="tt-compl-label">
                        Masse totale stack après traitement (g)
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={r.masse}
                        onChange={(e) =>
                          setField(i, "masse", e.target.value.replace(/[^0-9.,]/g, ""))
                        }
                        className={`tt-num-input ${!r.masse ? "tt-num-input--required" : ""}`}
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  {r.type === "Conditionnement" && isPlyAutoMode(r) && (
                    <div
                      style={{
                        marginTop: 8,
                        padding: "10px 12px",
                        borderRadius: 8,
                        background: "#0a2a35",
                        border: "1px solid #1a5a6a",
                        fontSize: 12,
                        lineHeight: 1.45,
                        color: "#b8d4de",
                      }}
                    >
                      <strong style={{ color: "#E6FF3E" }}>
                        Mode : pesée stack + répartition automatique
                      </strong>
                      <div style={{ marginTop: 6 }}>
                        {toFloat(r.masse) > 0 && r.repartitionStackActive ? (
                          <>
                            Les masses par pli sont calculées pour que leur somme =
                            la masse stack saisie :{" "}
                            <span style={{ color: "#7ab8cc" }}>
                              masse_pli = masse_banc × (M_stack / Σ masse_banc)
                            </span>
                          </>
                        ) : (
                          <>Saisissez la masse stack pesée pour répartir automatiquement sur les plis.</>
                        )}
                      </div>
                    </div>
                  )}

                  {r.type === "Conditionnement" &&
                    r.masse &&
                    r.masseConditionnementRef != null && (
                      <div className="tt-gain-massique tt-gain-massique--pos">
                        <span className="tt-gain-label">Humidité après conditionnement (stack)</span>
                        <span className="tt-gain-value">
                          {r.humiditeApresConditionnement != null
                            ? `${parseFloat(r.humiditeApresConditionnement).toFixed(4)} %`
                            : "--"}
                        </span>
                        <span className="tt-gain-formula">
                          H(M_stack, M_init, H_banc moy.)
                        </span>
                      </div>
                    )}

                  {r.type === "Sechage" && r.masse && r.masseSechageRef != null && (
                    <div className="tt-gain-row">
                      <div
                        className={`tt-gain-massique ${
                          parseFloat(r.perteMassiqueSechage || 0) >= 0
                            ? "tt-gain-massique--pos"
                            : "tt-gain-massique--neg"
                        }`}
                      >
                        <span className="tt-gain-label">Perte massique séchage</span>
                        <span className="tt-gain-value">
                          {r.perteMassiqueSechage ?? "--"} %
                        </span>
                        <span className="tt-gain-formula">(Msechage − Mref) / Mref × 100</span>
                      </div>

                      <div
                        className={`tt-gain-massique ${
                          r.humiditeApresSechage != null &&
                          (parseFloat(r.humiditeApresSechage) < 3 ||
                            parseFloat(r.humiditeApresSechage) > 10)
                            ? "tt-gain-massique--neg"
                            : "tt-gain-massique--pos"
                        }`}
                      >
                        <span className="tt-gain-label">Humidité après séchage</span>
                        <span className="tt-gain-value">
                          {r.humiditeApresSechage != null
                            ? `${parseFloat(r.humiditeApresSechage).toFixed(4)} %`
                            : "--"}
                        </span>
                        <span className="tt-gain-formula">cible : 3 – 10 %</span>
                      </div>
                    </div>
                  )}

                  {r.nbPlis > 0 && r.type === "Conditionnement" && (
                    <label className="tt-compl-checkbox">
                      <input
                        type="checkbox"
                        checked={r.mesureParPli}
                        onChange={() => toggleParPli(i)}
                      />
                      <span>
                        Saisie manuelle par pli ({r.nbPlis} plis)
                        {!r.mesureParPli && (
                          <span style={{ color: "#5a8a99", fontWeight: 400 }}>
                            {" "}
                            — décoché : pesée stack + répartition auto (voir ci-dessus)
                          </span>
                        )}
                      </span>
                    </label>
                  )}

                  {r.nbPlis > 0 && r.type === "Sechage" && (
                    <label className="tt-compl-checkbox">
                      <input
                        type="checkbox"
                        checked={r.mesureParPli}
                        onChange={() => toggleParPli(i)}
                      />
                      <span>
                        Saisie manuelle par pli ({r.nbPlis} plis)
                        {!r.mesureParPli && (
                          <span style={{ color: "#5a8a99", fontWeight: 400 }}>
                            {" "}
                            — répartition : M_imp_pli × (M_séch_stack / M_imp_stack)
                          </span>
                        )}
                      </span>
                    </label>
                  )}

                  {r.nbPlis === 0 && (
                    <div className="tt-compl-no-plis">
                      Nombre de plis non renseigné dans l'empilement.
                    </div>
                  )}

                  {r.plisReferencesLoading && (
                    <div className="tt-compl-mimp-bar">
                      <div className="tt-compl-mimp-loading">
                        Chargement des références par pli...
                      </div>
                    </div>
                  )}
{((r.type === "Conditionnement" || r.type === "Sechage") &&
  r.plis.length > 0) ||
  (r.mesureParPli && r.plis.length > 0) ? (
  <div className="tt-plis-table-wrap">
    <table className="tt-table">
      <thead>
        <tr>
          <th className="tt-th tt-th-pos">Pli</th>
          <th className="tt-th">PlyPN</th>
          {r.type === "Sechage" && (
            <th className="tt-th tt-th-num">M. Imprégnation (g)</th>
          )}
          {r.type === "Sechage" && (
            <th className="tt-th tt-th-num">M. Conditionnement (g)</th>
          )}
          {r.type === "Conditionnement" && (
            <th className="tt-th tt-th-num">M. anhydre pli (g)</th>
          )}
          {r.type === "Conditionnement" && (
            <th className="tt-th tt-th-num">M. cond. pli (g)</th>
          )}
          {r.type === "Sechage" && (
            <th className="tt-th tt-th-num">M. séch. pli (g)</th>
          )}
          {!(r.type === "Conditionnement" && isPlyAutoMode(r)) && (
            <th className="tt-th tt-th-num">Masse (g)</th>
          )}
          {r.type === "Sechage" && (
            <th className="tt-th tt-th-num">Humidité</th>
          )}
              {r.type === "Conditionnement" && (
      <th className="tt-th tt-th-num">Humidité pli</th>
    )}
        </tr>
      </thead>
      <tbody>
        {r.plis.map((p, j) => (
          <tr
            key={`${p.position}-${p.plyPn}-${j}`}
            className={j % 2 === 0 ? "tt-tr-even" : "tt-tr-odd"}
          >
            <td className="tt-td tt-td-center">
              <span className="tt-pos-badge">{p.position}</span>
            </td>

            <td className="tt-td">{p.plyPn}</td>

            {r.type === "Sechage" && (
              <td
                className="tt-td"
                style={{ textAlign: "center", color: "#5a8a99" }}
              >
                {p.masseImpregnation != null ? (
                  <span>{parseFloat(p.masseImpregnation).toFixed(2)} g</span>
                ) : (
                  <span style={{ color: "#3a5a6a" }}>--</span>
                )}
              </td>
            )}

            {r.type === "Sechage" && (
              <td
                className="tt-td"
                style={{ textAlign: "center", color: "#5a8a99" }}
              >
                {p.masseConditionnement != null ? (
                  <span>{parseFloat(p.masseConditionnement).toFixed(2)} g</span>
                ) : (
                  <span style={{ color: "#3a5a6a" }}>--</span>
                )}
              </td>
            )}

            {r.type === "Conditionnement" && (
              <td
                className="tt-td"
                style={{ textAlign: "center", color: "#5a8a99" }}
              >
                {p.masseAnhydre != null ? (
                  <span>{parseFloat(p.masseAnhydre).toFixed(2)} g</span>
                ) : (
                  <span style={{ color: "#3a5a6a" }}>--</span>
                )}
              </td>
            )}

            {r.type === "Conditionnement" && (
              <td
                className="tt-td"
                style={{ textAlign: "center", color: "#7ab8cc" }}
              >
                {(() => {
                  const mCond = masseCondPliFromFormule(p, r);
                  return mCond != null && Number.isFinite(mCond) ? (
                    <span>{parseFloat(mCond).toFixed(2)} g</span>
                  ) : (
                    <span style={{ color: "#3a5a6a" }}>--</span>
                  );
                })()}
              </td>
            )}

            {r.type === "Sechage" && (
              <td
                className="tt-td"
                style={{ textAlign: "center", color: "#7ab8cc" }}
              >
                {(() => {
                  const m =
                    masseSechPliFromFormule(p, r) ??
                    (p.masseSechCalc != null ? toFloat(p.masseSechCalc) : null);
                  return m != null && Number.isFinite(m) ? (
                    <span>{parseFloat(m).toFixed(2)} g</span>
                  ) : (
                    <span style={{ color: "#3a5a6a" }}>--</span>
                  );
                })()}
              </td>
            )}

            {!(r.type === "Conditionnement" && isPlyAutoMode(r)) && (
            <td className="tt-td">
              <input
                type="text"
                inputMode="decimal"
                value={p.masse}
                readOnly={isPlyAutoMode(r)}
                onChange={(e) =>
                  setPliField(
                    i,
                    j,
                    "masse",
                    e.target.value.replace(/[^0-9.,]/g, "")
                  )
                }
                className={`tt-cell-input tt-cell-input--num ${
                  !p.masse ? "tt-cell-input--required" : ""
                }`}
                placeholder="0.00"
                style={
                  isPlyAutoMode(r)
                    ? { opacity: 0.85, cursor: "not-allowed" }
                    : undefined
                }
              />
            </td>
            )}
  {r.type === "Conditionnement" && (
  <td className="tt-td" style={{ textAlign: "center" }}>
    {(() => {
      const h =
        p.humidite != null && p.humidite !== ""
          ? p.humidite
          : humiditeCondPliFromFormule(p, r);
      return h != null && h !== "" ? (
        <strong style={{ color: "#00B050" }}>
          {parseFloat(String(h).replace(",", ".")).toFixed(4)} %
        </strong>
      ) : (
        <span style={{ color: "#5a8a99" }}>--</span>
      );
    })()}
  </td>
)}

           {r.type === "Sechage" && (
  <td className="tt-td" style={{ textAlign: "center" }}>
    {(() => {
      const raw =
        p.humidite != null && p.humidite !== ""
          ? p.humidite
          : humiditeSechPliFromFormule(p, r);
      if (raw === "NA") {
        return <span style={{ color: "#aa8844" }}>N/A</span>;
      }
      if (raw == null || raw === "") {
        return <span style={{ color: "#5a8a99" }}>--</span>;
      }
      const num = parseFloat(String(raw).replace(",", "."));
      return (
        <strong
          style={{
            color: num < 3 || num > 10 ? "#C00000" : "#00B050",
          }}
        >
          {num.toFixed(4)} %
        </strong>
      );
    })()}
  </td>
)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
) : null}

                  {r.errorMsg && (
                    <div className="tt-msg tt-msg-error" style={{ marginTop: 4 }}>
                      {r.errorMsg}
                    </div>
                  )}

                  <button
                    type="button"
                    disabled={r.validating || !r.masse || rows.some((x) => x.validating)}
                    className="tt-btn-submit"
                    style={{ marginTop: 8, width: "100%" }}
                    onClick={() => handleValidateSingle(i)}
                  >
                    {r.validating ? "⏳ Enregistrement…" : "✔ Valider ce stack"}
                  </button>

                  {!r.cancelOpen ? (
                    <button
                      type="button"
                      className="tt-btn-cancel"
                      style={{ marginTop: 6, width: "100%" }}
                      disabled={rows.some((x) => x.validating)}
                      onClick={() => setCancelField(i, { cancelOpen: true, errorMsg: "" })}
                    >
                      🚫 Annuler ce stack (non traité, ajouté par erreur…)
                    </button>
                  ) : (
                    <div
                      style={{
                        marginTop: 6,
                        padding: 10,
                        border: "1px solid #7a3b3b",
                        borderRadius: 6,
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      <label htmlFor={`tt-cancel-motif-${i}`} style={{ fontSize: 12 }}>
                        Motif de l'annulation de {r.stackId}
                      </label>
                      <select
                        id={`tt-cancel-motif-${i}`}
                        value={r.cancelMotif || ""}
                        onChange={(e) => setCancelField(i, { cancelMotif: e.target.value, errorMsg: "" })}
                      >
                        <option value="">— Choisir —</option>
                        <option value="Ajouté par erreur">Ajouté par erreur</option>
                        <option value="Doublon">Doublon (déjà dans une autre session)</option>
                        <option value="Mauvais stack scanné">Mauvais stack scanné</option>
                        <option value="Non traité">Non traité (sorti avant traitement)</option>
                      </select>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          className="tt-btn-submit"
                          style={{ flex: 1 }}
                          disabled={r.validating || !r.cancelMotif}
                          onClick={() => handleCancelSingle(i)}
                        >
                          {r.validating ? "⏳ Annulation…" : "Confirmer l'annulation"}
                        </button>
                        <button
                          type="button"
                          className="tt-btn-cancel"
                          style={{ flex: 1 }}
                          disabled={r.validating}
                          onClick={() => setCancelField(i, { cancelOpen: false, cancelMotif: "", errorMsg: "" })}
                        >
                          Retour
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {globalError && (
          <div className="tt-msg tt-msg-error" style={{ margin: "0 16px 10px" }}>
            {globalError}
          </div>
        )}

        <div className="tt-compl-footer">
          <button type="button" className="tt-btn-cancel" onClick={onClose}>
            ✕ Fermer
          </button>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              color: "#5a8a99",
              fontSize: 12,
            }}
          >
            {allValidated
              ? "✅ Tous validés — fermeture…"
              : `${rows.length - validatedCount} stack${
                  rows.length - validatedCount > 1 ? "s" : ""
                } restant${rows.length - validatedCount > 1 ? "s" : ""}`}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   COMPOSANT PRINCIPAL
══════════════════════════════════════════ */

export default function TraitementThermique() {
  const navigate = useNavigate();

  /* ── State général ── */
  const [operator,        setOperator]        = useState("");
  const [now,             setNow]             = useState(new Date());
  const [temperature,     setTemperature]     = useState("");
  const [numEnceinte,     setNumEnceinte]     = useState("");
  const [showEnceinteScanner, setShowEnceinteScanner] = useState(false);
  const [sechage,         setSechage]         = useState(makeBloc());
  const [conditionnement, setConditionnement] = useState(makeBloc());



  /* ── State stacks disponibles ── */
  const [stackPns, setStackPns] = useState([]);

  /* ── State sessions actives (timers) ── */
  const [activeSessions, setActiveSessions] = useState([]);

  /* ── State retrait ── */
  const [showRetrait,    setShowRetrait]    = useState(false);
  const [retraitSession, setRetraitSession] = useState(null);

  /* ── State pending (stacks à mesurer) ── */
  const [pendingQueue,    setPendingQueue]    = useState([]);
  const [showCompletion,  setShowCompletion]  = useState(false);
  const [completionGroup, setCompletionGroup] = useState(null);

  /* ── State soumission ── */
  const [sending, setSending] = useState(false);
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState("");



  /* ── Opérateur ── */
  useEffect(() => {
    const raw = localStorage.getItem("user");
    if (raw) {
      try { setOperator(JSON.parse(raw).username || ""); } catch (e) {}
    }
  }, []);

  /* ── Horloge ── */
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  /* ── Notifications permission ── */
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  /* ── Restaurer sessions depuis localStorage ── */
  useEffect(() => {
    const saved = localStorage.getItem(LS_KEY);
    if (!saved) return;
    try {
      const sessions = JSON.parse(saved);
      const valid    = sessions.filter((s) =>
        (s.endTimeSechage         && new Date(s.endTimeSechage)         > new Date()) ||
        (s.endTimeConditionnement && new Date(s.endTimeConditionnement) > new Date())
      );
      setActiveSessions(valid);
      if (valid.length < sessions.length) localStorage.setItem(LS_KEY, JSON.stringify(valid));
    } catch (e) {
      localStorage.removeItem(LS_KEY);
    }
  }, []);

  /* ── Charger stacks disponibles ── */
  async function reloadStacks() {
    try {
      const res = await apiFetch("/api/empilement/list/traitement-thermique");
      if (!res.ok) return;
      const data = await res.json().catch(() => ({}));
      const raw  = Array.isArray(data.items) ? data.items : [];
      const ids  = raw.map((it) => String(it.StackId || "").trim()).filter(Boolean);
      setStackPns(Array.from(new Set(ids)));
    } catch (e) {
      console.error(e);
    }
  }

  useEffect(() => { reloadStacks(); }, []);

  /* ── Charger immédiatement les stacks en attente (tous postes / navigateurs) ── */
  useEffect(() => {
    apiFetch("/api/traitement-thermique/pending")
      .then(async (res) => {
        if (!res.ok) {
          console.warn("[TT pending] API", res.status);
          return null;
        }
        return res.json().catch(() => ({}));
      })
      .then((data) => {
        const items = data?.items || [];
        if (!items.length) return;
        setPendingQueue((prev) => {
          const merged = [...prev];
          items.forEach((it) => {
            const key = `${it.sessionTime}-${it.type}-${it.stackId}`;
            if (!merged.find((p) => `${p.sessionTime}-${p.type}-${p.stackId}` === key)) {
              merged.push(it);
            }
          });
          return merged;
        });
      })
      .catch((e) => console.error("[TT pending] chargement initial:", e));
  }, []);

  function handleScanEnceinte(results) {
  if (!results?.length) return;
  const v = (results[0].rawValue || "").trim();
  setNumEnceinte(v);
  setShowEnceinteScanner(false);
}

  /* ── Polling ── */
const handlePending = useCallback(item => {
  setPendingQueue(prev => {
    // ✅ clé avec type
    const key = `${item.sessionTime}-${item.type}-${item.stackId}`;
    if (prev.find(p => `${p.sessionTime}-${p.type}-${p.stackId}` === key)) return prev;
    return [...prev, item];
  });
}, []);
  usePendingNotifications(activeSessions, handlePending);

  /* ── Terminer session ── */
  async function handleKillSession(session) {
    const confirmed = window.confirm(
      `⚠️ Terminer la session du ${new Date(session.time).toLocaleTimeString("fr-FR")} ?\n\nTous les stacks encore en cours passeront en "En attente de mesure".`
    );
    if (!confirmed) return;

    try {
      const res = await apiFetch("/api/traitement-thermique/kill", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ time: session.time }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError("❌ " + (d.message || "Erreur serveur"));
        return;
      }

      // 1. Retirer la session des timers actifs
      setActiveSessions((prev) => {
        const updated = prev.filter((s) => s.time !== session.time);
        localStorage.setItem(LS_KEY, JSON.stringify(updated));
        return updated;
      });

      // 2. Récupérer les stacks à mesurer
      const pendingRes = await apiFetch("/api/traitement-thermique/pending");
      if (pendingRes.ok) {
        const data     = await pendingRes.json().catch(() => ({}));
        const newItems = data.items || [];
        newItems.forEach(handlePending);

        if (newItems.length > 0) {
          // ✅ Ouvrir directement la modal
          const firstTime = newItems[0].sessionTime;
          const items     = newItems.filter((p) => p.sessionTime === firstTime);
         setPendingQueue((prev) => {
  const merged = [...prev];
  items.forEach((it) => {
    const key = `${it.sessionTime}-${it.type}-${it.stackId}`;
    if (!merged.find((p) => `${p.sessionTime}-${p.type}-${p.stackId}` === key)) {
      merged.push(it);
    }
  });
  return merged;
});

setTimeout(() => {
  openCompletion();
}, 0);
        } else {
          setSuccess("✅ Session arrêtée — aucun stack en attente.");
        }
      }
    } catch (err) {
      setError("❌ " + (err.message || "Erreur lors de l'arrêt."));
    }
  }

  /* ── Ajouter un stack ── */
  async function handleAddStack(type, rangee, stackId) {
    const key     = `rangee${rangee}`;
    const setter  = type === "Sechage" ? setSechage : setConditionnement;
    const bloc    = type === "Sechage" ? sechage    : conditionnement;
    const current = bloc[key];

    if (current.length >= 30)                    { alert("Maximum 30 stacks par rangée."); return; }
    if (current.some((s) => s.stackId === stackId)) { alert("Stack déjà présent."); return; }

    for (const [, b] of [["Sechage", sechage], ["Conditionnement", conditionnement]]) {
      for (const r of ["A", "B"]) {
        if (b[`rangee${r}`].some((s) => s.stackId === stackId)) {
          alert(`${stackId} est déjà présent dans un autre bloc/rangée.`);
          return;
        }
      }
    }

    const res = await apiFetch("/api/stack/ajout/traitement-thermique", {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ stackId }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      alert(d.message || "Erreur ajout stack");
      return;
    }

    setter((prev) => ({ ...prev, [key]: [...prev[key], makeStack(stackId)] }));
    await reloadStacks();
  }

  /* ── Retirer le dernier stack d'une rangée ── */
  async function handleRemoveStack(type, rangee) {
    const key     = `rangee${rangee}`;
    const setter  = type === "Sechage" ? setSechage : setConditionnement;
    const bloc    = type === "Sechage" ? sechage    : conditionnement;
    const current = bloc[key];

    if (!current.length) return;
    const last = current[current.length - 1];

    const res = await apiFetch("/api/stack/supprimer/traitement-thermique", {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ stackId: last.stackId }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      alert(d.message || "Erreur suppression stack");
      return;
    }

    setter((prev) => ({ ...prev, [key]: prev[key].slice(0, -1) }));
    await reloadStacks();
  }

  /* ── Compter total stacks ── */
  const totalStacks = useMemo(() => (
    sechage.rangeeA.length +
    sechage.rangeeB.length +
    conditionnement.rangeeA.length +
    conditionnement.rangeeB.length
  ), [sechage, conditionnement]);

  /* ── Timer expiré ── */
  function handleTimerExpire() {
    apiFetch("/api/traitement-thermique/pending")
      .then((r) => r.json())
      .then((data) => (data.items || []).forEach(handlePending))
      .catch(console.error);
  }

  /* ── Ouvrir modal retrait ── */
  function openRetrait(session) {
    setRetraitSession(session);
    setShowRetrait(true);
  }

  /* ── Ouvrir modal complétion ── */
  // ✅ Ne traite qu'une session à la fois
async function openCompletion() {
  if (pendingQueue.length === 0) return;

  const items = [...pendingQueue];

  const baseItems = items.map((it) => ({
    ...it,
    masseInit: null,
    masseImpregnation: null,
    masseConditionnementStack: null,
    masseAnhydreStack: null,
    masseAnhydreParPli: {},
    masseImpregnationParPli: {},
    masseConditionnementParPli: {},
    masseInitialeParPli: {},
    humiditeMerlinParPli: {},
    humiditeBancMoyenne: null,
    referencesReady: false,
    plisReferencesReady: false,
  }));

  setCompletionGroup({
    sessionTime: items[0]?.sessionTime ?? null,
    items: baseItems,
    loadingMasse: true,
    loadingAnhydre: true,
    loadingImpregPli: true,
  });

  setShowCompletion(true);

  const stackIds = [...new Set(items.map((x) => x.stackId).filter(Boolean))];
  if (!stackIds.length) {
    setCompletionGroup((prev) =>
      prev
        ? {
            ...prev,
            loadingMasse: false,
            loadingAnhydre: false,
            loadingImpregPli: false,
          }
        : prev
    );
    return;
  }

  try {
    const res = await apiFetch(
      `/api/traitement-thermique/references-completion?stackIds=${encodeURIComponent(
        stackIds.join(",")
      )}`
    );

    const data = res.ok ? await res.json().catch(() => ({})) : {};
    const refs = data.items || {};

    setCompletionGroup((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((it) => {
                const ref = refs[it.stackId] || {};

                return {
                  ...it,
                  masseInit: ref.masseInit ?? null,
                  masseImpregnation: ref.masseImpregnation ?? null,
                  masseConditionnementStack: ref.masseConditionnementStack ?? null,
                  masseAnhydreStack: ref.masseAnhydreStack ?? null,
                  masseAnhydreParPli: ref.masseAnhydreParPli ?? {},
                  masseImpregnationParPli: ref.masseImpregnationParPli ?? {},
                  masseConditionnementParPli: ref.masseConditionnementParPli ?? {},
                  masseInitialeParPli: ref.masseInitialeParPli ?? {},
                  humiditeMerlinParPli: ref.humiditeMerlinParPli ?? {},
                  humiditeBancMoyenne: ref.humiditeBancMoyenne ?? null,
                  humiditeApresImpregnationStack: ref.humiditeApresImpregnation ?? null,
                  humiditeApresConditionnementStack:
                    ref.humiditeApresConditionnementStack ?? null,
                  humiditeApresConditionnement:
                    ref.humiditeApresConditionnementStack != null
                      ? formatHumiditePct(ref.humiditeApresConditionnementStack, 4)
                      : null,
                  plisInfo: ref.plisInfo?.length ? ref.plisInfo : it.plisInfo ?? [],
                  nbPlis: Math.max(
                    Number(it.nbPlis) || 0,
                    ref.plisInfo?.length || 0,
                    (ref.plisInfo?.length ? ref.plisInfo : it.plisInfo || []).length
                  ),
                  humiditeCondParPosition: ref.humiditeCondParPosition ?? {},
                  humiditeCondParPli: ref.humiditeCondParPli ?? {},
                  referencesReady: true,
                  plisReferencesReady: true,
                };
              }),
            loadingMasse: false,
            loadingAnhydre: false,
            loadingImpregPli: false,
          }
        : prev
    );
  } catch (e) {
    console.error("Erreur openCompletion:", e);
    setCompletionGroup((prev) =>
      prev
        ? {
            ...prev,
            loadingMasse: false,
            loadingAnhydre: false,
            loadingImpregPli: false,
          }
        : prev
    );
  }
}

  /* ── Complétion réussie ── */
  function handleCompletionSuccess() {
  setPendingQueue([]);
  setShowCompletion(false);
  setCompletionGroup(null);
  setSuccess("✅ Masses enregistrées avec succès !");
}


  /* ── Stack validé individuellement ── */
function handleStackValidated(sessionTime, stackId, type) {
  setPendingQueue(prev =>
    prev.filter(p =>
      !(p.sessionTime === sessionTime && p.stackId === stackId && p.type === type)
    )
  );
}

  /* ── Retrait réussi ── */
  function handleRetraitSuccess(retraits) {
    setActiveSessions((prev) => {
      const updated = prev.map((s) => {
        if (s.time !== retraitSession.time) return s;
        const newSession = { ...s };
        for (const retrait of retraits) {
          const blocKey   = `bloc${retrait.type}`;
          const rangeeKey = `rangee${retrait.rangee}`;
          if (!newSession[blocKey]) continue;
          newSession[blocKey] = {
            ...newSession[blocKey],
            [rangeeKey]: (newSession[blocKey][rangeeKey] || []).map((st) =>
              st.stackId === retrait.stackId ? { ...st, status: "retire" } : st
            ),
          };
        }
        return newSession;
      });
      localStorage.setItem(LS_KEY, JSON.stringify(updated));
      return updated;
    });
    setShowRetrait(false);
    setSuccess("✅ Stack(s) retiré(s) — en attente de mesure.");
  }

  /* ── Soumission du formulaire ── */
  async function handleSubmit(e) {
    e.preventDefault();
    setError(""); setSuccess("");

    if (!numEnceinte.trim())                        return setError("Merci de saisir le numéro d'enceinte.");
    if (!temperature)                               return setError("Merci de saisir la température.");
    if (!sechage.active && !conditionnement.active) return setError("Activez au moins un traitement (Séchage ou Conditionnement).");

    if (sechage.active) {
      if (!sechage.duree) return setError("Séchage : durée manquante.");
      if (sechage.rangeeA.length + sechage.rangeeB.length === 0)
        return setError("Séchage : ajoutez au moins un stack.");
    }
    if (conditionnement.active) {
      if (!conditionnement.duree) return setError("Conditionnement : durée manquante.");
      if (conditionnement.rangeeA.length + conditionnement.rangeeB.length === 0)
        return setError("Conditionnement : ajoutez au moins un stack.");
    }
    if (totalStacks > 120) return setError("Maximum 120 stacks au total.");

    const payload = {
      operateur:   operator,
      numEnceinte: numEnceinte.trim(),
      temperature: toFloat(temperature),
      sechage: sechage.active ? {
        duree:   toFloat(sechage.duree),
        rangeeA: sechage.rangeeA.map((s) => ({ stackId: s.stackId })),
        rangeeB: sechage.rangeeB.map((s) => ({ stackId: s.stackId })),
      } : null,
      conditionnement: conditionnement.active ? {
        duree:   toFloat(conditionnement.duree),
        rangeeA: conditionnement.rangeeA.map((s) => ({ stackId: s.stackId })),
        rangeeB: conditionnement.rangeeB.map((s) => ({ stackId: s.stackId })),
      } : null,
    };

    try {
      setSending(true);
      let res = await apiFetch("/api/traitement-thermique", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      if (res.status === 409) {
        const d = await res.json().catch(() => ({}));
        // Stack déjà traité : retraitement possible après confirmation explicite
        if (d.code === "CONFIRMATION_RETRAITEMENT" && Array.isArray(d.stacks)) {
          if (!window.confirm(`${d.message}\n\nOK = le traiter à nouveau, Annuler = revenir à la saisie.`)) {
            setError("Session non créée : retirez le stack déjà traité ou confirmez le retraitement.");
            return;
          }
          res = await apiFetch("/api/traitement-thermique", {
            method:  "POST",
            headers: { "Content-Type": "application/json" },
            body:    JSON.stringify({
              ...payload,
              confirmerRetraitement: d.stacks.map((s) => s.stackId),
            }),
          });
        } else {
          throw new Error(d.message || "Stack déjà présent dans une autre session.");
        }
      }
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || "Erreur serveur");
      }
      const result = await res.json();

      const newSession = {
        time:                   result.time,
        numEnceinte:            numEnceinte.trim(),
        endTimeSechage:         result.endTimeSechage         || null,
        endTimeConditionnement: result.endTimeConditionnement || null,
        blocSechage:            payload.sechage,
        blocConditionnement:    payload.conditionnement,
      };
      const updated = [...activeSessions, newSession];
      setActiveSessions(updated);
      localStorage.setItem(LS_KEY, JSON.stringify(updated));

      setNumEnceinte("");
      setTemperature("");
      setSechage(makeBloc());
      setConditionnement(makeBloc());

      const parts = [];
      if (result.endTimeSechage)
        parts.push(`🔥 Séchage fin ${new Date(result.endTimeSechage).toLocaleTimeString("fr-FR")}`);
      if (result.endTimeConditionnement)
        parts.push(`💧 Conditionnement fin ${new Date(result.endTimeConditionnement).toLocaleTimeString("fr-FR")}`);
      setSuccess(`✅ Traitement démarré — ${parts.join(" · ")}`);

      await reloadStacks();
    } catch (err) {
      setError("❌ " + (err.message || "Erreur d'envoi."));
    } finally {
      setSending(false);
    }
  }

  const timeStr = now.toLocaleTimeString("fr-FR");
  const dateStr = now.toLocaleDateString("fr-FR", {
    weekday: "short",
    day:     "2-digit",
    month:   "short",
    year:    "numeric",
  });

  return (
    <div className="tt-page">
      <div className="tt-card">

        {/* HEADER */}
        <div className="tt-header">
          <span>Traitement Thermique</span>
          <span className="tt-header-sub">Étuve · Rangées A &amp; B</span>
        </div>

        {/* NAV */}
        <div className="tt-nav">
          <button type="button" className="tt-btn-back" onClick={() => navigate("/menu")}>
            ← Retour menu
          </button>
          <div className="tt-clock">
            <div className="tt-clock-time">{timeStr}</div>
            <div className="tt-clock-date">{dateStr}</div>
          </div>
        </div>

        {/* OPÉRATEUR */}
        <div className="tt-operator">
          <span className="tt-operator-dot" />
          <span>{operator ? operator.toUpperCase() : "OPÉRATEUR NON CONNECTÉ"}</span>
          {pendingQueue.length > 0 && (
            <button type="button" className="tt-notif-badge" onClick={openCompletion}>
              🔔 {pendingQueue.length} à mesurer
            </button>
          )}
        </div>

        {/* SESSIONS ACTIVES */}
        {activeSessions.length > 0 && (
          <div className="tt-sessions-wrap">
            {activeSessions.map((session, si) => (
              <div key={si} className="tt-session-block">
                <div className="tt-session-label">
                  <span>
                    Session du {new Date(session.time).toLocaleTimeString("fr-FR")} — {session.numEnceinte}
                  </span>
                  <div className="tt-session-actions">
                    <button type="button" className="tt-btn-retrait" onClick={() => openRetrait(session)}>
                      ⚠️ Retrait anticipé
                    </button>
                    <button type="button" className="tt-btn-kill" onClick={() => handleKillSession(session)}>
                      🛑 Terminer
                    </button>
                  </div>
                </div>
                <div className="tt-timers-row">
                  {session.endTimeSechage && new Date(session.endTimeSechage) > new Date() && (
                    <BlocTimer
                      type="Sechage"
                      numEnceinte={session.numEnceinte}
                      endTime={session.endTimeSechage}
                      onExpire={handleTimerExpire}
                    />
                  )}
                  {session.endTimeConditionnement && new Date(session.endTimeConditionnement) > new Date() && (
                    <BlocTimer
                      type="Conditionnement"
                      numEnceinte={session.numEnceinte}
                      endTime={session.endTimeConditionnement}
                      onExpire={handleTimerExpire}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* STACKS EN ATTENTE DE MESURE */}
        {pendingQueue.length > 0 && (
          <div className="tt-pending-section">
            <div className="tt-pending-header">
              <span className="tt-pending-title">⏳ En attente de mesure</span>
              <span className="tt-pending-count">
                {pendingQueue.length} stack{pendingQueue.length > 1 ? "s" : ""}
              </span>
            </div>
            <div className="tt-pending-list">
              {pendingQueue.map((item, i) => (
                <div key={i} className="tt-pending-item">
                  <div className="tt-pending-item-left">
                    <span className="tt-pending-stack-id">{item.stackId}</span>
                    <span className="tt-pending-meta">
                      {item.type === "Sechage" ? "🔥 Séchage" : "💧 Conditionnement"} · Rangée {item.rangee}
                    </span>
                    {item.retireAt && (
                      <span className="tt-pending-retire-at">
                        ⚠️ Retiré à {new Date(item.retireAt).toLocaleTimeString("fr-FR")}
                      </span>
                    )}
                  </div>
                  <div className="tt-pending-item-right">
                    {item.pendingType === "retrait_anticipe" && (
                      <span className="tt-pending-badge tt-pending-badge--retrait">Retrait anticipé</span>
                    )}
                    {item.pendingType === "fin_timer" && (
                      <span className="tt-pending-badge tt-pending-badge--fin">Fin timer</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button type="button" className="tt-btn-pending-complete" onClick={openCompletion}>
              ✏️ Saisir les masses ({pendingQueue.length})
            </button>
          </div>
        )}

        {/* FORMULAIRE */}
        <form onSubmit={handleSubmit} className="tt-form">

          {/* NUMÉRO D'ENCEINTE */}
         {/* NUMÉRO D'ENCEINTE */}
<div className="tt-section">
  <div className="tt-section-label">🏭 Numéro d'enceinte</div>
  <div className="tt-select-row">
    <input
      type="text"
      value={numEnceinte}
      onChange={(e) => setNumEnceinte(e.target.value)}
      className="tt-num-input"
      placeholder="Ex : E01"
      style={{ flex: 1 }}
    />
    <button
      type="button"
      className="tt-scan-btn"
      onClick={() => setShowEnceinteScanner(true)}
      title="Scanner QR enceinte"
    >
      📷
    </button>
  </div>
</div>

          {/* TEMPÉRATURE */}
          <div className="tt-section">
            <div className="tt-section-label">🌡️ Température (°C)</div>
            <input
              type="text"
              inputMode="decimal"
              value={temperature}
              onChange={(e) => setTemperature(e.target.value.replace(/[^0-9.,]/g, ""))}
              className="tt-num-input tt-temp-input"
              placeholder="Ex : 80"
            />
          </div>

          {/* BLOC SÉCHAGE */}
          <BlocSection
            type="Sechage"
            bloc={sechage}
            onToggle={() => setSechage((prev) => ({ ...prev, active: !prev.active }))}
            onSetDuree={(v) => setSechage((prev) => ({ ...prev, duree: v }))}
            onAddStack={handleAddStack}
            onRemoveStack={handleRemoveStack}
            stackPns={stackPns}
          />

          {/* BLOC CONDITIONNEMENT */}
          <BlocSection
            type="Conditionnement"
            bloc={conditionnement}
            onToggle={() => setConditionnement((prev) => ({ ...prev, active: !prev.active }))}
            onSetDuree={(v) => setConditionnement((prev) => ({ ...prev, duree: v }))}
            onAddStack={handleAddStack}
            onRemoveStack={handleRemoveStack}
            stackPns={stackPns}
          />

          {/* COMPTEUR TOTAL */}
          <div className="tt-total-counter">
            <span className="tt-total-val">{totalStacks}</span>
            <span className="tt-total-label">stack{totalStacks > 1 ? "s" : ""} au total</span>
            <span className="tt-total-max">/ 120 max</span>
            <div className="tt-progress-bar">
              <div
                className="tt-progress-fill"
                style={{ width: `${Math.min((totalStacks / 120) * 100, 100)}%` }}
              />
            </div>
          </div>

          {/* Messages */}
          {error   && <div className="tt-msg tt-msg-error">{error}</div>}
          {success && <div className="tt-msg tt-msg-success">{success}</div>}

          {/* Submit */}
          <div className="tt-submit-row">
            <button type="submit" disabled={sending} className="tt-btn-submit">
              {sending ? "⏳ Démarrage…" : "▶ Démarrer le traitement"}
            </button>
            <button type="button" className="tt-btn-time">
              Horodatage
              <div className="tt-btn-time-sub">{timeStr}</div>
            </button>
          </div>

        </form>
      </div>
        {/* MODAL SCANNER ENCEINTE */}
{showEnceinteScanner && (
  <div className="tt-modal-backdrop" onClick={() => setShowEnceinteScanner(false)}>
    <div className="tt-modal-scanner" onClick={(e) => e.stopPropagation()}>
      <div className="tt-modal-title">📷 Scanner le QR code de l'enceinte</div>
      <div style={{ borderRadius: 10, overflow: "hidden" }}>
        <Scanner
          onScan={handleScanEnceinte}
          onError={(e) => console.error(e)}
          constraints={{ facingMode: "environment" }}
        />
      </div>
      <button
        type="button"
        className="tt-btn-close-scanner"
        onClick={() => setShowEnceinteScanner(false)}
      >
        ✕ Fermer
      </button>
    </div>
  </div>
)}


      {/* MODAL RETRAIT */}
      {showRetrait && retraitSession && (
        <RetraitModal
          session={retraitSession}
          onClose={() => setShowRetrait(false)}
          onSuccess={handleRetraitSuccess}
        />
      )}

      {/* MODAL COMPLETION */}
     {showCompletion && completionGroup && (
  <CompletionModal
    pendingItems={completionGroup.items}
    sessionTime={completionGroup.sessionTime}
    loadingMasse={completionGroup.loadingMasse}
    loadingAnhydre={completionGroup.loadingAnhydre}
    loadingImpregPli={completionGroup.loadingImpregPli}
    onClose={() => {
  setShowCompletion(false);
  setCompletionGroup(null);
}}
    onSuccess={handleCompletionSuccess}
    onStackValidated={handleStackValidated}
  />
)}

      <Style />
    </div>
  );
}

/* ══════════════════════════════════════════
   STYLES
══════════════════════════════════════════ */

function Style() {
  return (
    <style>{`
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

      /* ── PAGE & CARD ─────────────────────── */
      .tt-page {
        min-height: 100vh;
        width: 100%;
        background: radial-gradient(ellipse at top, #01283a 0%, #010F14 70%);
        display: flex;
        justify-content: center;
        align-items: flex-start;
        padding: 12px;
        font-family: 'Inter', 'Segoe UI', sans-serif;
      }
      .tt-card {
        width: 100%;
        max-width: 680px;
        background: #011e2b;
        border: 1px solid #1a4a5a;
        border-radius: 14px;
        overflow: hidden;
        box-shadow: 0 8px 40px rgba(0,0,0,0.6);
      }

      /* ── HEADER ──────────────────────────── */
      .tt-header {
        background: linear-gradient(135deg, #E6FF3E, #c8e800);
        color: #000;
        padding: 12px 16px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;
      }
      .tt-header > span { font-size: 14px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; }
      .tt-header-sub    { font-size: 10px; font-weight: 500; opacity: 0.65; }

      /* ── NAV ─────────────────────────────── */
      .tt-nav {
        background: #010f14;
        padding: 8px 14px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #1a4a5a;
      }
      .tt-btn-back {
        background: transparent;
        color: #7ab8cc;
        border: 1px solid #1a4a5a;
        border-radius: 6px;
        padding: 5px 10px;
        font-size: 11px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
        white-space: nowrap;
      }
      .tt-btn-back:hover { background: #1a4a5a; color: #fff; }
      .tt-clock-time { color: #E6FF3E; font-size: 14px; font-weight: 700; font-variant-numeric: tabular-nums; text-align: right; }
      .tt-clock-date { color: #5a8a99; font-size: 10px; text-align: right; }
      .tt-ref-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.tt-ref-badge--ok {
  background: rgba(0, 176, 80, 0.18);
  color: #7dffb1;
  border: 1px solid rgba(0, 176, 80, 0.45);
}

.tt-ref-badge--loading {
  background: rgba(122, 184, 204, 0.12);
  color: #7ab8cc;
  border: 1px solid rgba(122, 184, 204, 0.28);
}

      /* ── OPÉRATEUR ───────────────────────── */
      .tt-operator {
        display: flex;
        align-items: center;
        gap: 10px;
        background: linear-gradient(90deg, #012836, #013d50);
        border: 1px solid #1a5a6a;
        border-radius: 10px;
        margin: 12px 14px 0;
        padding: 10px 14px;
        color: #fff;
        font-size: 13px;
        font-weight: 700;
      }
      .tt-operator-dot {
        width: 9px;
        height: 9px;
        background: #00B050;
        border-radius: 50%;
        box-shadow: 0 0 6px #00B050;
        flex-shrink: 0;
      }
      .tt-notif-badge {
        margin-left: auto;
        background: #C00000;
        color: #fff;
        border: none;
        border-radius: 20px;
        padding: 4px 12px;
        font-size: 11px;
        font-weight: 700;
        cursor: pointer;
        animation: tt-pulse 1.5s infinite;
      }
      @keyframes tt-pulse { 0%,100%{opacity:1} 50%{opacity:0.55} }

      /* ── SESSIONS ACTIVES ────────────────── */
      .tt-sessions-wrap { padding: 12px 14px 0; display: flex; flex-direction: column; gap: 10px; }
      .tt-session-block { background: #001520; border: 1px solid #1a4a5a; border-radius: 12px; overflow: hidden; }
      .tt-session-label {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 14px;
        background: #012030;
        color: #7ab8cc;
        font-size: 11px;
        font-weight: 700;
        border-bottom: 1px solid #1a4a5a;
        gap: 8px;
        flex-wrap: wrap;
      }
      .tt-session-actions { display: flex; gap: 6px; align-items: center; flex-shrink: 0; }
      .tt-btn-retrait {
        background: #400000;
        color: #ff9090;
        border: 1px solid #C00000;
        border-radius: 6px;
        padding: 4px 10px;
        font-size: 11px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.2s;
      }
      .tt-btn-retrait:hover { background: #C00000; color: #fff; }
      .tt-btn-kill {
        background: #2a0a00;
        color: #ff6030;
        border: 1px solid #ff6030;
        border-radius: 6px;
        padding: 4px 10px;
        font-size: 11px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.2s;
      }
      .tt-btn-kill:hover { background: #ff6030; color: #fff; }
      .tt-timers-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; padding: 10px; }

      /* ── MINUTEUR ────────────────────────── */
      .tt-timer-card {
        background: #001a2e;
        border: 2px solid #E6FF3E;
        border-radius: 10px;
        padding: 12px;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .tt-timer-urgent { border-color: #C00000; animation: tt-pulse 1s infinite; }
      .tt-timer-header { display: flex; align-items: center; gap: 8px; }
      .tt-timer-icon   { font-size: 18px; flex-shrink: 0; }
      .tt-timer-title  { color: #E6FF3E; font-size: 11px; font-weight: 800; text-transform: uppercase; }
      .tt-timer-sub    { color: #7ab8cc; font-size: 10px; margin-top: 2px; }
      .tt-timer-badge  {
        margin-left: auto;
        background: #1a4000;
        color: #E6FF3E;
        border: 1px solid #E6FF3E;
        border-radius: 5px;
        padding: 2px 6px;
        font-size: 9px;
        font-weight: 800;
        letter-spacing: 0.06em;
        flex-shrink: 0;
        white-space: nowrap;
      }
      .tt-timer-badge--urgent { background: #400000; color: #ff9090; border-color: #C00000; }
      .tt-timer-display {
        font-size: 28px;
        font-weight: 800;
        color: #E6FF3E;
        font-variant-numeric: tabular-nums;
        text-align: center;
      }
      .tt-timer-urgent .tt-timer-display { color: #ff9090; }
      .tt-timer-progress-wrap { width: 100%; height: 5px; background: #1a2800; border-radius: 4px; overflow: hidden; }
      .tt-timer-progress-fill {
        height: 100%;
        background: linear-gradient(90deg, #E6FF3E, #a0c000);
        border-radius: 4px;
        transition: width 1s linear;
      }
      .tt-timer-urgent .tt-timer-progress-fill { background: linear-gradient(90deg, #ff6060, #C00000); }
      .tt-timer-footer { display: flex; justify-content: space-between; }
      .tt-timer-info   { color: #5a8a99; font-size: 10px; }
      .tt-timer-info strong { color: #7ab8cc; }

      /* ── FORMULAIRE ──────────────────────── */
      .tt-form { padding: 12px 14px 20px; display: flex; flex-direction: column; gap: 12px; }

      /* ── SECTIONS ────────────────────────── */
      .tt-section {
        background: #012030;
        border: 1px solid #1a4a5a;
        border-radius: 12px;
        padding: 12px 14px;
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      .tt-section-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #7ab8cc; }
      .tt-temp-input    { max-width: 200px; font-size: 22px !important; text-align: center; }

      /* ── BLOC SÉCHAGE / CONDITIONNEMENT ──── */
      .tt-bloc-section {
        background: #010f1c;
        border: 1px solid #1a4a5a;
        border-radius: 12px;
        overflow: hidden;
      }
      .tt-bloc-section--active { border-color: #1a6a8a; }
      .tt-bloc-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 14px;
        cursor: pointer;
        user-select: none;
        background: #012030;
        transition: background 0.2s;
      }
      .tt-bloc-header:hover { background: #013040; }
      .tt-bloc-title        { color: #E6FF3E; font-size: 14px; font-weight: 800; }
      .tt-bloc-toggle-row   { display: flex; align-items: center; gap: 10px; }
      .tt-bloc-count        { color: #7ab8cc; font-size: 12px; }
      .tt-toggle-switch {
        width: 40px;
        height: 22px;
        background: #1a4a5a;
        border-radius: 11px;
        position: relative;
        transition: background 0.2s;
        flex-shrink: 0;
      }
      .tt-toggle-switch--on { background: #00B050; }
      .tt-toggle-knob {
        width: 16px;
        height: 16px;
        background: #fff;
        border-radius: 50%;
        position: absolute;
        top: 3px;
        left: 3px;
        transition: left 0.2s;
      }
      .tt-toggle-switch--on .tt-toggle-knob { left: 21px; }
      .tt-bloc-body { padding: 12px 14px; display: flex; flex-direction: column; gap: 10px; }
      .tt-duree-row { display: flex; align-items: center; gap: 10px; }
      .tt-duree-input { max-width: 140px; }
      .tt-plyn-label  { color: #ffffff; font-size: 12px; font-weight: 500; }

      /* ── RANGÉES ─────────────────────────── */
      .tt-rangees { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
      .tt-rangee  {
        background: #011828;
        border: 1px solid #1a5a4a;
        border-radius: 10px;
        padding: 10px;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .tt-rangee-header { display: flex; justify-content: space-between; align-items: center; }
      .tt-rangee-badge  {
        background: #1a4a3a;
        color: #00B050;
        border: 1px solid #00B050;
        border-radius: 6px;
        padding: 3px 8px;
        font-size: 11px;
        font-weight: 800;
      }
      .tt-rangee-count { color: #E6FF3E; font-size: 14px; font-weight: 800; font-variant-numeric: tabular-nums; }
      .tt-rangee-max   { color: #4a6a00; font-size: 10px; }

      /* ── STACK TAGS ──────────────────────── */
      .tt-stack-tags { display: flex; flex-wrap: wrap; gap: 4px; }
      .tt-stack-tag  {
        background: #1a2800;
        border: 1px solid #4a6a00;
        border-radius: 20px;
        padding: 2px 8px;
        font-size: 10px;
      }
      .tt-stack-tag-pn { color: #E6FF3E; font-weight: 700; }

      /* ── SELECT & ACTIONS ────────────────── */
      .tt-select-row { display: flex; gap: 5px; }
      .tt-action-row { display: flex; gap: 5px; margin-top: 5px; }
      .tt-select-input {
        flex: 1;
        background: #003818;
        color: #7aff9a;
        border: 1px solid #005020;
        border-radius: 7px;
        padding: 7px 9px;
        font-size: 11px;
        font-weight: 600;
        outline: none;
        transition: border 0.2s;
        min-width: 0;
      }
      .tt-select-input:focus { border-color: #00B050; }
      .tt-scan-btn {
        width: 64px;
        height: 64px;
        flex-shrink: 0;
        background: #012030;
        border: 1px solid #1a4a5a;
        border-radius: 7px;
        font-size: 16px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .tt-scan-btn:hover { background: #1a4a5a; }
      .tt-btn-add, .tt-btn-remove {
        flex: 1;
        height: 32px;
        border-radius: 7px;
        font-weight: 700;
        font-size: 11px;
        cursor: pointer;
        transition: opacity 0.2s;
        white-space: nowrap;
        min-width: 0;
      }
      .tt-btn-add            { background: #00B050; color: #000; border: 2px solid #008040; }
      .tt-btn-add:disabled   { opacity: 0.4; cursor: not-allowed; }
      .tt-btn-remove         { background: #C00000; color: #fff; border: 2px solid #800000; }
      .tt-btn-remove:disabled { opacity: 0.4; cursor: not-allowed; }
      .tt-selected-badge {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 5px;
        background: #1a2800;
        border: 1px solid #E6FF3E;
        border-radius: 7px;
        padding: 5px 10px;
        font-size: 10px;
        color: #E6FF3E;
      }
      .tt-selected-badge strong { flex: 1; word-break: break-all; }
      .tt-clear-btn { background: transparent; border: none; color: #ff8080; cursor: pointer; font-size: 13px; font-weight: 700; }

      /* ── DROPDOWN ────────────────────────── */
      .tt-dropdown {
        position: absolute;
        left: 0;
        right: 0;
        margin-top: 3px;
        max-height: 160px;
        overflow-y: auto;
        background: #002818;
        border: 1px solid #005020;
        border-radius: 7px;
        z-index: 50;
        box-shadow: 0 8px 24px rgba(0,0,0,0.5);
      }
      .tt-dropdown-item {
        padding: 7px 10px;
        color: #aaffcc;
        font-size: 11px;
        font-weight: 600;
        cursor: pointer;
        border-bottom: 1px solid #003818;
        transition: background 0.15s;
      }
      .tt-dropdown-item:hover      { background: #003d20; }
      .tt-dropdown-item:last-child { border-bottom: none; }

      /* ── STACKS EN ATTENTE ───────────────── */
      .tt-pending-section {
        background: #1a0808;
        border: 2px solid #C00000;
        border-radius: 12px;
        margin: 12px 14px 0;
        overflow: hidden;
        animation: tt-pulse 2s infinite;
      }
      .tt-pending-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        background: #2a0808;
        padding: 10px 14px;
        border-bottom: 1px solid #C00000;
      }
      .tt-pending-title { color: #ff9090; font-size: 13px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em; }
      .tt-pending-count { color: #fff; background: #C00000; border-radius: 20px; padding: 2px 10px; font-size: 12px; font-weight: 700; }
      .tt-pending-list  { display: flex; flex-direction: column; gap: 1px; max-height: 240px; overflow-y: auto; }
      .tt-pending-item  {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 9px 14px;
        background: #1a0808;
        border-bottom: 1px solid #2a1010;
        transition: background 0.15s;
      }
      .tt-pending-item:hover    { background: #2a1010; }
      .tt-pending-item-left     { display: flex; flex-direction: column; gap: 3px; }
      .tt-pending-stack-id      { color: #ff9090; font-size: 13px; font-weight: 800; }
      .tt-pending-meta          { color: #7a5a5a; font-size: 11px; }
      .tt-pending-retire-at     { color: #ffa050; font-size: 10px; }
      .tt-pending-badge {
        border-radius: 6px;
        padding: 3px 8px;
        font-size: 10px;
        font-weight: 700;
        white-space: nowrap;
      }
      .tt-pending-badge--retrait { background: #400000; color: #ff9090; border: 1px solid #C00000; }
      .tt-pending-badge--fin     { background: #1a3000; color: #E6FF3E; border: 1px solid #E6FF3E; }
      .tt-btn-pending-complete {
        width: 100%;
        padding: 12px;
        background: #C00000;
        color: #fff;
        border: none;
        font-size: 13px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        cursor: pointer;
        transition: opacity 0.2s;
      }
      .tt-btn-pending-complete:hover { opacity: 0.85; }

      /* ── INPUT NUMÉRIQUE ─────────────────── */
      .tt-num-input {
        width: 100%;
        background: #010f14;
        color: #fff;
        border: 1px solid #1a4a5a;
        border-radius: 7px;
        padding: 9px 10px;
        font-size: 15px;
        font-weight: 700;
        text-align: center;
        outline: none;
        transition: border 0.2s;
      }
      .tt-num-input:focus        { border-color: #E6FF3E; box-shadow: 0 0 0 2px rgba(230,255,62,0.1); }
      .tt-num-input--required    { border-color: #C00000; }

      /* ── TOTAL COUNTER ───────────────────── */
      .tt-total-counter {
        display: flex;
        align-items: baseline;
        gap: 6px;
        flex-wrap: wrap;
        background: #1a2800;
        border: 1px solid #4a6000;
        border-radius: 10px;
        padding: 10px 14px;
      }
      .tt-total-val    { font-size: 28px; font-weight: 800; color: #E6FF3E; font-variant-numeric: tabular-nums; }
      .tt-total-label  { font-size: 13px; color: #b8d900; font-weight: 600; }
      .tt-total-max    { font-size: 11px; color: #6a8a00; margin-left: auto; }
      .tt-progress-bar {
        width: 100%;
        height: 4px;
        background: #2a3a00;
        border-radius: 4px;
        margin-top: 4px;
        flex-basis: 100%;
      }
      .tt-progress-fill {
        height: 100%;
        background: linear-gradient(90deg, #E6FF3E, #a0c000);
        border-radius: 4px;
        transition: width 0.3s;
      }

      /* ── MESSAGES ────────────────────────── */
      .tt-msg         { border-radius: 8px; padding: 10px 13px; font-size: 13px; font-weight: 600; }
      .tt-msg-error   { background: #2a0808; border: 1px solid #C00000; color: #ff9090; }
      .tt-msg-success { background: #002810; border: 1px solid #00B050; color: #80ff90; }

      /* ── SUBMIT ──────────────────────────── */
      .tt-submit-row { display: flex; gap: 10px; align-items: stretch; }
      .tt-btn-submit {
        flex: 1;
        background: linear-gradient(135deg, #00B050, #008a3c);
        color: #000;
        border: none;
        border-radius: 10px;
        padding: 13px 10px;
        font-size: 13px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.07em;
        cursor: pointer;
        transition: opacity 0.2s, transform 0.1s;
        box-shadow: 0 4px 16px rgba(0,176,80,0.3);
      }
      .tt-btn-submit:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
      .tt-btn-submit:disabled             { opacity: 0.5; cursor: not-allowed; }
      .tt-btn-time {
        min-width: 90px;
        flex-shrink: 0;
        background: #C00000;
        color: #fff;
        border: 2px solid #7F0000;
        border-radius: 8px;
        padding: 8px;
        font-weight: 700;
        font-size: 11px;
        cursor: default;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 3px;
      }
      .tt-btn-time-sub { font-size: 10px; font-variant-numeric: tabular-nums; }

      /* ── MODAL BACKDROP ──────────────────── */
      .tt-modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,0.88);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999;
        padding: 16px;
        backdrop-filter: blur(6px);
      }

      /* ── MODAL RETRAIT ───────────────────── */
      .tt-modal-retrait {
        width: 100%;
        max-width: 500px;
        max-height: 90vh;
        background: #011e2b;
        border: 2px solid #C00000;
        border-radius: 16px;
        display: flex;
        flex-direction: column;
        box-shadow: 0 0 50px rgba(192,0,0,0.3);
        overflow: hidden;
      }
      .tt-compl-header {
        background: linear-gradient(135deg, #00B050, #007a38);
        color: #000;
        padding: 14px 18px;
        flex-shrink: 0;
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 13px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.08em;
      }
      .tt-compl-header--retrait { background: linear-gradient(135deg, #C00000, #800000); color: #fff; }
      .tt-compl-close {
        background: rgba(0,0,0,0.2);
        border: none;
        color: inherit;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        font-size: 14px;
        font-weight: 700;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .tt-retrait-list          { flex: 1; overflow-y: auto; padding: 8px 16px; }
      .tt-retrait-empty         { color: #5a8a99; font-size: 12px; text-align: center; padding: 20px 0; }
      .tt-retrait-chariot       { margin-bottom: 12px; }
      .tt-retrait-chariot-label {
        color: #E6FF3E;
        font-size: 12px;
        font-weight: 800;
        text-transform: uppercase;
        margin-bottom: 6px;
        letter-spacing: 0.06em;
      }
      .tt-retrait-rangee        { margin-bottom: 8px; }
      .tt-retrait-rangee-label  { color: #7ab8cc; font-size: 11px; font-weight: 700; margin-bottom: 4px; }
      .tt-retrait-stacks        { display: flex; flex-wrap: wrap; gap: 5px; }
      .tt-retrait-stack {
        display: flex;
        align-items: center;
        gap: 5px;
        background: #012030;
        border: 1px solid #1a4a5a;
        border-radius: 20px;
        padding: 4px 10px;
        font-size: 11px;
        color: #7ab8cc;
        cursor: pointer;
        transition: all 0.2s;
      }
      .tt-retrait-stack--selected { background: #400000; border-color: #C00000; color: #ff9090; }
      .tt-retrait-stack:hover     { border-color: #C00000; }
      .tt-retrait-check           { font-size: 12px; }
      .tt-retrait-selection {
        background: #400000;
        color: #ff9090;
        border-top: 1px solid #C00000;
        padding: 8px 16px;
        font-size: 12px;
        font-weight: 700;
        text-align: center;
        flex-shrink: 0;
      }

      /* ── MODAL COMPLETION ────────────────── */
      .tt-modal-completion {
        width: 100%;
        max-width: 640px;
        max-height: 90vh;
        background: #011e2b;
        border: 2px solid #00B050;
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        box-shadow: 0 0 50px rgba(0,176,80,0.25);
        overflow: hidden;
      }
      .tt-compl-scroll {
        flex: 1;
        overflow-y: auto;
        padding: 12px 16px;
        display: flex;
        max-height: calc(90vh - 120px);
        flex-direction: column;
        gap: 12px;
      }
      .tt-compl-row {
        background: #012030;
        border: 1px solid #1a4a5a;
        border-radius: 12px;
        padding: 12px;
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      .tt-compl-row-header {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
        padding-bottom: 8px;
        border-bottom: 1px solid #1a4a5a;
      }
      .tt-compl-stack-id    { color: #E6FF3E; font-size: 14px; font-weight: 800; }
      .tt-compl-stack-meta  { color: #7ab8cc; font-size: 11px; flex: 1; }
      .tt-compl-badge-retrait {
        background: #400000;
        color: #ff9090;
        border: 1px solid #C00000;
        border-radius: 6px;
        padding: 2px 8px;
        font-size: 10px;
        font-weight: 700;
      }
      .tt-compl-badge-fin {
        background: #1a3000;
        color: #E6FF3E;
        border: 1px solid #E6FF3E;
        border-radius: 6px;
        padding: 2px 8px;
        font-size: 10px;
        font-weight: 700;
      }
      .tt-compl-fields   { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
      .tt-compl-field    { display: flex; flex-direction: column; gap: 6px; }
      .tt-compl-label    { color: #7ab8cc; font-size: 11px; font-weight: 700; }
      .tt-compl-checkbox {
        display: flex;
        align-items: center;
        gap: 8px;
        background: #0a2a3a;
        border: 1px solid #1a4a5a;
        border-radius: 8px;
        padding: 8px 12px;
        color: #7ab8cc;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
      }
      .tt-compl-checkbox input { width: 16px; height: 16px; cursor: pointer; accent-color: #E6FF3E; }
      .tt-compl-no-plis { color: #ffa050; font-size: 11px; font-weight: 600; }
      .tt-plis-table-wrap {
        overflow-x: auto;
        border-radius: 8px;
        border: 1px solid #1a4a5a;
        margin-top: 10px;
        overflow: hidden;
      }

      /* ── MASSE IMPRÉGNATION ──────────────── */
      .tt-compl-mimp-bar {
        background: #010f1c;
        border: 1px solid #1a4a5a;
        border-radius: 8px;
        padding: 8px 12px;
      }
      .tt-compl-mimp-loading { color: #5a8a99; font-size: 11px; }
      .tt-compl-mimp-error   { color: #ffa050; font-size: 11px; font-weight: 600; }
      .tt-compl-mimp-info    { display: flex; flex-wrap: wrap; gap: 16px; }
      .tt-compl-mimp-item    { color: #7ab8cc; font-size: 11px; }
      .tt-compl-mimp-item strong { color: #fff; }

      /* ── GAIN MASSIQUE ───────────────────── */
      .tt-gain-row {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
      }
      .tt-gain-row > .tt-gain-massique { flex: 1 1 0; min-width: 160px; }
      .tt-gain-massique {
        display: flex;
        flex-direction: column;
        gap: 4px;
        border-radius: 10px;
        padding: 10px 14px;
        border: 2px solid;
      }
      .tt-gain-massique--pos { background: #001a0a; border-color: #00B050; }
      .tt-gain-massique--neg { background: #1a0808; border-color: #C00000; }
      .tt-gain-label {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #7ab8cc;
      }
      .tt-gain-value {
        font-size: 28px;
        font-weight: 800;
        font-variant-numeric: tabular-nums;
      }
      .tt-gain-massique--pos .tt-gain-value { color: #00B050; }
      .tt-gain-massique--neg .tt-gain-value { color: #C00000; }
      .tt-gain-formula { font-size: 10px; color: #4a6a7a; font-style: italic; }

      /* ── TABLE PLIS ──────────────────────── */
      .tt-table { width: 100%; border-collapse: collapse; font-size: 12px; min-width: 300px; }
      .tt-th {
        background: #0a2a3a;
        color: #7ab8cc;
        padding: 8px 10px;
        text-align: left;
        font-weight: 600;
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        border-bottom: 2px solid #1a4a5a;
        border-right: 1px solid #1a4a5a;
        white-space: nowrap;
      }
      .tt-th:last-child { border-right: none; }
      .tt-th-pos { width: 50px; }
      .tt-th-num { width: 130px; }
      .tt-th-pn, .tt-td-pn { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .tt-tr-even { background: #011828; }
      .tt-tr-odd  { background: #012030; }
      .tt-td {
        padding: 6px 10px;
        border-right: 1px solid #1a4a5a;
        border-top: 1px solid rgba(255,255,255,0.05);
        border-bottom: 1px solid #0a2a3a;
        vertical-align: middle;
        color:#fff;
      }
      .tt-td:last-child { border-right: none; }
      .tt-td-center     { text-align: center; }
      .tt-pos-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 26px;
        height: 26px;
        background: #0a3a50;
        color: #7ab8cc;
        border-radius: 50%;
        font-weight: 700;
        font-size: 11px;
      }
      .tt-cell-input {
        width: 100%;
        background: #010f14;
        color: #fff;
        border: 1px solid #1a4a5a;
        border-radius: 6px;
        padding: 6px 7px;
        font-size: 12px;
        outline: none;
        transition: border 0.2s;
      }
      .tt-cell-input:focus         { border-color: #E6FF3E; }
      .tt-cell-input--num          { text-align: center; font-weight: 700; }
      .tt-cell-input--required     { border-color: #C00000; }

      /* ── FOOTER MODAL ────────────────────── */
      .tt-compl-footer {
        display: flex;
        gap: 10px;
        padding: 14px 16px;
        border-top: 1px solid #1a4a5a;
        flex-shrink: 0;
      }
      .tt-btn-cancel {
        height: 44px;
        padding: 0 20px;
        border-radius: 8px;
        background: #C00000;
        color: #fff;
        border: 2px solid #800000;
        font-weight: 700;
        font-size: 13px;
        cursor: pointer;
        white-space: nowrap;
      }
      .tt-btn-cancel:hover        { opacity: 0.85; }
      .tt-compl-footer .tt-btn-submit { padding: 12px 16px; font-size: 14px; }

      /* ── SCANNER MODAL ───────────────────── */
      .tt-modal-scanner {
        width: 100%;
        max-width: 360px;
        background: #011e2b;
        border: 1px solid #E6FF3E;
        border-radius: 14px;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .tt-modal-title { color: #E6FF3E; font-size: 13px; font-weight: 700; text-align: center; }
      .tt-btn-close-scanner {
        background: #C00000;
        color: #fff;
        border: none;
        border-radius: 8px;
        padding: 10px;
        font-weight: 700;
        font-size: 13px;
        cursor: pointer;
      }

      /* ══ RESPONSIVE ══════════════════════════ */
      @media (min-width: 540px) {
        .tt-card          { max-width: 760px; }
        .tt-header > span { font-size: 16px; }
        .tt-form          { padding: 14px 18px 22px; }
        .tt-sessions-wrap { padding: 14px 18px 0; }
      }
      @media (min-width: 768px) {
        .tt-card          { max-width: 960px; }
        .tt-header > span { font-size: 18px; }
        .tt-operator      { margin: 16px 24px 0; }
        .tt-form          { padding: 16px 24px 26px; gap: 16px; }
        .tt-sessions-wrap { padding: 16px 24px 0; }
        .tt-section       { padding: 16px 18px; border-radius: 14px; }
        .tt-section-label { font-size: 12px; }
        .tt-rangees       { gap: 14px; }
        .tt-timer-display { font-size: 32px; }
        .tt-num-input     { font-size: 18px; }
        .tt-temp-input    { font-size: 28px !important; max-width: 260px; }
        .tt-modal-completion { max-width: 800px; }
        .tt-modal-retrait    { max-width: 600px; }
      }
      @media (min-width: 1024px) {
        .tt-card             { max-width: 1200px; }
        .tt-header > span    { font-size: 20px; }
        .tt-operator         { margin: 18px 32px 0; font-size: 16px; }
        .tt-form             { padding: 20px 32px 32px; gap: 18px; }
        .tt-sessions-wrap    { padding: 20px 32px 0; }
        .tt-section          { padding: 18px 22px; }
        .tt-section-label    { font-size: 13px; }
        .tt-timer-display    { font-size: 38px; }
        .tt-num-input        { font-size: 20px; }
        .tt-temp-input       { font-size: 32px !important; max-width: 300px; }
        .tt-bloc-title       { font-size: 16px; }
        .tt-btn-add, .tt-btn-remove { height: 38px; font-size: 13px; }
        .tt-select-input     { font-size: 13px; }
        .tt-total-val        { font-size: 36px; }
        .tt-btn-submit       { font-size: 15px; padding: 15px; }
        .tt-modal-completion { max-width: 960px; }
      }
      @media (min-width: 1400px) {
        .tt-card             { max-width: 1500px; }
        .tt-header > span    { font-size: 24px; }
        .tt-operator         { margin: 20px 48px 0; font-size: 18px; padding: 14px 28px; }
        .tt-form             { padding: 24px 48px 40px; gap: 22px; }
        .tt-sessions-wrap    { padding: 24px 48px 0; }
        .tt-section          { padding: 22px 28px; border-radius: 16px; }
        .tt-section-label    { font-size: 15px; }
        .tt-timer-display    { font-size: 46px; }
        .tt-num-input        { font-size: 24px; padding: 13px; }
        .tt-temp-input       { font-size: 38px !important; max-width: 360px; }
        .tt-bloc-title       { font-size: 18px; }
        .tt-rangee           { padding: 14px; }
        .tt-rangee-badge     { font-size: 13px; }
        .tt-btn-add, .tt-btn-remove { height: 44px; font-size: 14px; }
        .tt-select-input     { font-size: 14px; padding: 9px 11px; }
        .tt-stack-tag-pn     { font-size: 12px; }
        .tt-total-val        { font-size: 48px; }
        .tt-btn-submit       { font-size: 18px; padding: 18px; }
        .tt-btn-time         { min-width: 140px; font-size: 13px; padding: 12px; }
        .tt-modal-completion { max-width: 1200px; }
      }
    `}</style>
  );
}